/**
 * THE ONE PLACE THIS INTERFACE TALKS TO THE SERVER.
 *
 * Same origin, JSON in and JSON out. In production the SPA and the Hono app are one Worker, so
 * every path here is written as `/api/...` and there is no base URL to configure, no CORS and no
 * token to attach.
 *
 * Every function below matches an endpoint that exists in `api/src/index.ts`. There are no others.
 */

/**
 * Reads an answer's body to its end and lets it go, for an answer whose status says everything the
 * screen acts on. A browser counts a request finished only once its body is read, so a body left
 * unread keeps the request open in Chrome for as long as the page lives. A body that breaks off as it
 * is read changes nothing its status said.
 */
async function letGo(response: Response): Promise<void> {
  try {
    await response.arrayBuffer();
  } catch {
    // The status has said what this answer is.
  }
}

/*
 * A REQUEST WITH NO ANSWER (slice V3, after the owner's walk was replayed, which found a dropped
 * connection said as Stripe being slow, and a request that stalled leaving the screen waiting with
 * no end). The requests a screen waits on before it can say anything, every ask through the request
 * door, every poll for Stripe's page, and the two sign-in requests a code step makes, are each given
 * a time limit, and say why there was no answer: it never reached the Worker, because `fetch` itself
 * was refused, which is how a browser says the connection dropped; or none came in time, the answer's
 * body included, and the request is let go. Past the limit with the browser saying it is offline, it
 * is the connection too.
 */

/** How long a request a screen waits on is given before it is let go: ten seconds. */
export const REQUEST_LIMIT_MS = 10_000;

/** Why a request had no answer: it never reached the Worker, or none came within its time. */
class Unanswered extends Error {
  constructor(readonly why: 'offline' | 'stalled') {
    super(why);
  }
}

/** Whether the browser says it is offline: only a clear no from it is read as one. */
const saysOffline = (): boolean =>
  typeof navigator !== 'undefined' && (navigator as { onLine?: unknown }).onLine === false;

/**
 * Sends a request and reads its answer with `read`, within `REQUEST_LIMIT_MS`: past it, the request
 * is let go and this throws `Unanswered`, `stalled`, or `offline` where the browser says so. A `fetch`
 * that is refused throws `Unanswered`, `offline`. Whatever `read` throws, it throws as it is.
 */
async function within<T>(
  path: string,
  init: RequestInit,
  read: (response: Response) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const past = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Unanswered(saysOffline() ? 'offline' : 'stalled'));
      controller.abort();
    }, REQUEST_LIMIT_MS);
  });
  const answered = fetch(path, { ...init, signal: controller.signal }).then(read, () => {
    throw new Unanswered('offline');
  });
  try {
    return await Promise.race([answered, past]);
  } finally {
    clearTimeout(timer);
  }
}

/** Why a request that threw had no answer, or null where it had one this code could not read. */
const unansweredBy = (error: unknown): Unanswered['why'] | null =>
  error instanceof Unanswered ? error.why : null;

/**
 * What the door needs from /api/health: whether sign-in is open on this deployment, and whether a
 * code can go by text on this host too, so the sign-in steps take a mobile number (ops #76, home
 * #11). Texts are never open where sign-in is not.
 */
export interface Health {
  readonly signIn: boolean;
  readonly signInByText: boolean;
}

/** Sign-in not open, and so no texts either. */
const CLOSED: Health = { signIn: false, signInByText: false };

/**
 * Asks /api/health whether sign-in is open, and whether by text as well. Anything short of a clear
 * yes is a no: a failed request, an error status and an answer this code cannot read all close the
 * door, because a Sign in button that leads to a refusal is worse than a sentence saying sign-in is
 * not open, and a field that takes a number where no text can go would leave her waiting for one.
 * An error's body is read to its end and let go (`letGo`).
 */
export async function getHealth(): Promise<Health> {
  try {
    const response = await fetch('/api/health', { headers: { accept: 'application/json' } });
    if (!response.ok) {
      await letGo(response);
      return CLOSED;
    }
    const body = (await response.json()) as { signIn?: unknown; signInByText?: unknown } | null;
    const signIn = body?.signIn === true;
    return { signIn, signInByText: signIn && body?.signInByText === true };
  } catch {
    return CLOSED;
  }
}

/**
 * What her session's code proved, and so where a fresh code goes and what a screen names: her email
 * address, or her mobile number in E.164, texted (home #11). A number comes only where the host can
 * text, since only there can a session by number be made.
 */
export interface SignedInWith {
  readonly channel: 'email' | 'text';
  /** The address, or the number in E.164, as /api/me names it. */
  readonly value: string;
}

/**
 * What /api/me says: signed out, or signed in with the address or number the code proved and
 * whether a home is linked, and, while this session's fresh code holds, until when, as the Worker's
 * instant in milliseconds (`freshUntil`, round four, slice M3, `docs/seam.md` P59), absent while
 * none stands; and how many invitations wait for a code to that address or number, which only a
 * code opens (`invitationsWaiting`, `docs/seam.md` P118), absent while none does.
 */
export type Me =
  | { readonly signedIn: false }
  | {
      readonly signedIn: true;
      readonly signedInWith: SignedInWith;
      readonly hasMembership: boolean;
      readonly freshUntil?: number;
      readonly invitationsWaiting?: number;
    };

/**
 * What /api/me says the session's code proved: an address as `email`, as it always has, or a
 * session by number's `identifier` with `channel: 'sms'` (home #11); null for an answer naming
 * neither with something in it.
 */
function signedInWithOf(body: {
  readonly email?: unknown;
  readonly channel?: unknown;
  readonly identifier?: unknown;
}): SignedInWith | null {
  if (body.channel === 'sms') {
    return typeof body.identifier === 'string' && body.identifier !== ''
      ? { channel: 'text', value: body.identifier }
      : null;
  }
  if (body.channel !== undefined) return null;
  return typeof body.email === 'string' && body.email !== ''
    ? { channel: 'email', value: body.email }
    : null;
}

/**
 * Asks /api/me who is signed in. Anything short of a clear answer is signed out, because the door
 * is the screen that works with nothing known, and it carries the phone number: a sign-in naming
 * neither an address nor a number is none (`signedInWithOf`). A refusal's body is read to its end
 * and let go (`letGo`), or the 401 every signed-out visit gets would stay open for as long as the
 * page lives. A fresh code's instant is kept only as an instant the browser reads, and anything
 * else is none standing, which at worst draws the code step where the door would not have asked
 * for one. The invitations waiting are kept only as a whole number above none, and anything else is
 * none, which at worst leaves the notice undrawn until she next signs in.
 */
export async function getMe(): Promise<Me> {
  try {
    const response = await fetch('/api/me', { headers: { accept: 'application/json' } });
    if (!response.ok) {
      await letGo(response);
      return { signedIn: false };
    }
    const body = (await response.json()) as {
      signedIn?: unknown;
      email?: unknown;
      channel?: unknown;
      identifier?: unknown;
      hasMembership?: unknown;
      freshUntil?: unknown;
      invitationsWaiting?: unknown;
    } | null;
    const signedInWith = body?.signedIn === true ? signedInWithOf(body) : null;
    if (body === null || signedInWith === null) return { signedIn: false };
    const freshUntil = typeof body.freshUntil === 'string' ? Date.parse(body.freshUntil) : NaN;
    const waiting = body.invitationsWaiting;
    return {
      signedIn: true,
      signedInWith,
      hasMembership: body.hasMembership === true,
      ...(Number.isFinite(freshUntil) ? { freshUntil } : {}),
      ...(typeof waiting === 'number' && Number.isInteger(waiting) && waiting > 0
        ? { invitationsWaiting: waiting }
        : {}),
    };
  } catch {
    return { signedIn: false };
  }
}

/**
 * What asking for a code comes to. `asked` is the only good answer, and the Worker gives it whether
 * or not the address or number is on file, so it means a code may be on its way and never that one
 * was sent, until what came of the send says it was not (`codeSendState`, the owner's word of
 * 2026-10-20, never silent). `busy` is the Worker past its ceiling across every sign-in, which it
 * says the same way for every identifier before it looks one up: nothing was asked, and a press in
 * a minute may go through. `too_many_codes` is the one limit left on asking (the owner's word of
 * 2026-10-20): thirty codes in an hour for this address or number, which the Worker counts the same
 * whether or not it is on file, so nothing was asked and the page says so. `unreadable` is the
 * Worker's `400 bad_request`: what was sent is neither an email address nor a mobile number by its
 * rule, which it decides from what was typed alone (home #11).
 */
export type CodeAsked = 'asked' | 'busy' | 'too_many_codes' | 'closed' | 'failed' | 'unreadable';

/**
 * What asking for a code came to, and on `asked` the id the Worker's 202 names the ask by, or null
 * where it names none this code keeps (the owner's word of 2026-10-20, never silent): the page asks
 * what came of that one send by it (`codeSendState`, `lib/codeSent.ts`).
 */
export interface AskedForCode {
  readonly answer: CodeAsked;
  readonly ask: string | null;
}

/**
 * An ask's id as the Worker mints one, a version 4 UUID in lower case. Only that shape is kept, so
 * nothing else a body might hold ever reaches a path this tab asks.
 */
export const ASK_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * Asks POST /api/auth/code for a code to this identifier, an email address or a mobile number as she
 * typed it, which the Worker reads. 202 is asked, with the id its body names the ask by, 503 is
 * sign-in having closed, 400 is what was sent not being read as either, a 429 saying `busy` is the
 * Worker's ceiling, one saying `too_many_codes` its flood guard, and anything else, a failed
 * request, one with no answer in its time (`REQUEST_LIMIT_MS`) or another 429 included, did not go
 * through. A 202's body is read for its id and every other answer but a 429 for its status alone,
 * each body read to its end (`letGo`); a 202 whose body cannot be read is asked all the same, with
 * no id.
 */
export async function askForCode(identifier: string): Promise<AskedForCode> {
  const unnamed = (answer: CodeAsked): AskedForCode => ({ answer, ask: null });
  try {
    return await postJsonWithin('/api/auth/code', { identifier }, async (response) => {
      if (response.status === 429) return unnamed(codeRefusal(await errorOf(response)));
      if (response.status === 202) return { answer: 'asked', ask: await askIdOf(response) };
      await letGo(response);
      if (response.status === 503) return unnamed('closed');
      if (response.status === 400) return unnamed('unreadable');
      return unnamed('failed');
    });
  } catch {
    return unnamed('failed');
  }
}

/** The id a 202's body names its ask by, when it has the Worker's shape, its body read to its end. */
async function askIdOf(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { ask?: unknown } | null;
    return typeof body?.ask === 'string' && ASK_ID.test(body.ask) ? body.ask : null;
  } catch {
    return null;
  }
}

/** Why a code was not sent, as the Worker says it (`SEND_OUTCOMES` in `api/src/lib/delivery.ts`). */
export type SendFailure = 'stopped' | 'rate_limited' | 'not_enabled' | 'could_not_send';

const SEND_FAILURES: readonly SendFailure[] = [
  'stopped',
  'rate_limited',
  'not_enabled',
  'could_not_send',
];

/**
 * What came of one ask's send (the owner's word of 2026-10-20, never silent): `sending` while it is
 * out, `sent` once it went, `failed` with why; `gone` where the Worker holds no such ask; and
 * `unanswered` where this request had no answer it could read, which a later one may have.
 */
export type CodeSendState =
  'sending' | 'sent' | 'gone' | 'unanswered' | { readonly failed: SendFailure };

/**
 * Asks GET /api/auth/code/<ask> what came of that ask's send, within `REQUEST_LIMIT_MS`. 200 is read
 * for its state, a failure with a reason this code does not know as one that could not be sent; 404
 * is gone; anything else, a failed request or one with no answer in time included, is unanswered.
 * Every body is read to its end.
 */
export async function codeSendState(ask: string): Promise<CodeSendState> {
  try {
    return await within(
      `/api/auth/code/${encodeURIComponent(ask)}`,
      { method: 'GET', headers: { accept: 'application/json' } },
      async (response): Promise<CodeSendState> => {
        if (response.status === 404) {
          await letGo(response);
          return 'gone';
        }
        if (response.status !== 200) {
          await letGo(response);
          return 'unanswered';
        }
        const body = (await response.json()) as { state?: unknown; reason?: unknown } | null;
        if (body?.state === 'sending' || body?.state === 'sent') return body.state;
        if (body?.state !== 'failed') return 'unanswered';
        const reason = SEND_FAILURES.find((each) => each === body.reason) ?? 'could_not_send';
        return { failed: reason };
      },
    );
  } catch {
    return 'unanswered';
  }
}

/** What a 429 to a code asked for says: the Worker's `busy` or `too_many_codes`, else a failure. */
function codeRefusal(error: unknown): CodeAsked {
  if (error === 'busy') return 'busy';
  if (error === 'too_many_codes') return 'too_many_codes';
  return 'failed';
}

/** The `error` a 429's body names, or null, its body read to the end either way. */
async function errorOf(response: Response): Promise<unknown> {
  try {
    const body = (await response.json()) as { error?: unknown } | null;
    return body?.error ?? null;
  } catch {
    return null;
  }
}

/**
 * A code typed while the guess lock holds (the owner's word of 2026-10-20, never silent): until when
 * codes are paused, as the business's time of day on a 24-hour clock, `HH:MM`, and whether it falls
 * tomorrow, as the Worker says it, so the tab never reads the phone's clock; null where it says no
 * time this code can read.
 */
export interface CodesLocked {
  readonly locked: { readonly time: string; readonly tomorrow: boolean } | null;
}

/**
 * What entering a code comes to. On `signed_in` the Worker has set the session cookie; a
 * `CodesLocked` is the guess lock, which refused the try before it was compared.
 */
export type CodeEntered = 'signed_in' | 'not_accepted' | 'closed' | 'failed' | CodesLocked;

/**
 * Enters a code at POST /api/auth/verify, with the identifier it was asked for. Only a 200 saying ok
 * is signed in. A 401 is a code that does not work, whether wrong, used, ended or never asked, and a
 * 400 is read the same, because either way the code typed is not one that works. A 429 saying
 * `codes_locked` is the guess lock, with when it ends (`try_after`). 503 is sign-in having closed,
 * and anything else, a failed request or one with no answer in its time (`REQUEST_LIMIT_MS`) or
 * another 429 included, did not go through. Every answer but a 200's and a 429's is read for its
 * status alone, and each body is read to its end (`letGo`).
 */
export async function enterCode(identifier: string, code: string): Promise<CodeEntered> {
  try {
    return await postJsonWithin('/api/auth/verify', { identifier, code }, async (response) => {
      if (response.status === 200) {
        const body = (await response.json()) as { ok?: unknown } | null;
        return body?.ok === true ? 'signed_in' : 'failed';
      }
      if (response.status === 429) {
        const body = (await response.json()) as Fields | null;
        if (body?.error !== 'codes_locked') return 'failed';
        const at = body.try_after;
        const locked =
          isFields(at) &&
          typeof at.time === 'string' &&
          CLOCK_TIME.test(at.time) &&
          typeof at.tomorrow === 'boolean'
            ? { time: at.time, tomorrow: at.tomorrow }
            : null;
        return { locked };
      }
      await letGo(response);
      if (response.status === 401 || response.status === 400) return 'not_accepted';
      if (response.status === 503) return 'closed';
      return 'failed';
    });
  } catch {
    return 'failed';
  }
}

/**
 * A JSON POST to the Worker. The address or number and the code travel in the body, never in the
 * path or a query, so none of them lands in a log or in the browser's history.
 */
function postJson(path: string, body: Record<string, string>): Promise<Response> {
  return fetch(path, posting(body));
}

/** A JSON POST's method, headers and body. */
const posting = (body: Record<string, string>): RequestInit => ({
  method: 'POST',
  headers: { 'content-type': 'application/json', accept: 'application/json' },
  body: JSON.stringify(body),
});

/**
 * A JSON POST to the Worker, as `postJson`, that a screen waits on: its answer read with `read`
 * within `REQUEST_LIMIT_MS`, or `Unanswered` thrown (`within`).
 */
function postJsonWithin<T>(
  path: string,
  body: Record<string, string>,
  read: (response: Response) => Promise<T>,
): Promise<T> {
  return within(path, posting(body), read);
}

/** What a sign-out comes to: the Worker's own yes, or a sign-out that did not go through. */
export type SignOutAnswer = 'signed_out' | 'failed';

/**
 * Signs out: the Worker revokes the session row and clears the cookie, and answers 204. Anything
 * else, an error or a request that never reached it, did not go through, and the screen she is on
 * stays and says so (`components/SignOut.tsx`); once one has gone through, the screen asks /api/me
 * again, and that answer is the one it shows. Its answer, the Worker's 204 or any error, is read to
 * its end and let go (`letGo`).
 */
export async function signOut(): Promise<SignOutAnswer> {
  try {
    const response = await fetch('/api/auth/signout', { method: 'POST' });
    await letGo(response);
    return response.ok ? 'signed_out' : 'failed';
  } catch {
    return 'failed';
  }
}

/*
 * THE PLACES: what the Worker's `routes/places.ts` answers, read into the fields the screens draw.
 *
 * - FIELDS, NEVER THE ANSWER WHOLE. Each reader names the fields it keeps, and keeps each only when
 *   it has the type it should, so a field the Worker adds later reaches no screen until a reader
 *   here names it, and a value of the wrong type is drawn as nothing rather than drawn wrong.
 * - ABSENT IS NOTHING TO DRAW. The Worker leaves out a field ops withheld or never sent, and so does
 *   a reader, so a screen draws no empty row.
 * - THE WORKER DECIDES. What a person may ask for comes as the `offers` of each place, a visit and
 *   a home, in the seam's request types, and a screen draws a way to ask only for an offer it holds.
 *   What she would be offered but for the same ask already waiting on the office comes as `held`,
 *   read where a screen says so: ops takes one open at a time, and refuses a second.
 */

/** What a place read comes to. The screens act on each outcome (`components/Guarded.tsx`). */
export type PlaceAnswer<T> =
  | { readonly outcome: 'shown'; readonly place: T }
  /** 401: the session has ended, and the door is the way back in. */
  | { readonly outcome: 'signed_out' }
  /**
   * 404: no household to show, which is also how a membership ops revokes mid-session reads. On an
   * invoice's own page alone, `arriving` is the Worker saying the invoice is not in her copy yet
   * while her first fill is still arriving (ops's plan, section 7, ask 5), which it says of every id
   * it does not hold then, whosever it is; and with it `invited`, the Worker saying a client's door
   * ops pushed for the address her session proved waits for her code, which the page asks for (the
   * review of 7e33d61, finding 1).
   */
  | { readonly outcome: 'no_home'; readonly arriving?: true; readonly invited?: true }
  /** Anything else, a failed request included: it did not go through. */
  | { readonly outcome: 'failed' };

/** An invoice she can pay from home now, as the nav counts it: its id and what is still to pay. */
export interface Payable {
  readonly id: string;
  /** Ops's balance, above nothing. */
  readonly balanceCents: number;
}

/**
 * The household a place is about: its id, its name once it has come, and whether she pays here,
 * which the nav draws the Payments tab from. `payments` is there, and true, only when the Worker
 * says true: for a client ops has granted, and never for a member, who is shown no money.
 *
 * And the nav's marks (provisional P49), each there only when the Worker sends it in its shape, so
 * the nav never counts what it cannot read: `payable`, the invoices she can pay from home now, a
 * client's alone; `onItsWay`, each invoice owed whose payment is on its way as Payments draws it
 * from the Worker's fields, by ops's balance, a client's alone, which Home says (round six, slice
 * S3); `owedUncounted`, true while she owes an invoice `payable` leaves out, which the
 * saved method pays, at any stage of its charge, or no way from home can pay, a client's alone, so
 * Home never says there is nothing to pay; `newMessages`, how many things the office has said since
 * she last opened Messages; and `howWasIt`, true while a clean of hers waits for "How was it?".
 */
export interface HouseholdShown {
  readonly id: string;
  readonly name?: string;
  readonly payments?: true;
  readonly payable?: readonly Payable[];
  readonly onItsWay?: readonly Payable[];
  readonly owedUncounted?: true;
  readonly newMessages?: number;
  readonly howWasIt?: true;
}

/**
 * A visit as a screen draws it: its home, its day, its window, the cleaner's first name and its
 * status. The home is ops's site id, which names it among several by its label (`homeNameOf`).
 */
export interface Visit {
  readonly id: string;
  /** Ops's site id for the home it is at. */
  readonly homeId?: string;
  /** The business's day, `YYYY-MM-DD`. */
  readonly date?: string;
  /** Local times, `HH:MM`. Never on a clean that is under way. */
  readonly window?: { readonly start: string; readonly end: string };
  /** The first name, and nothing more. */
  readonly cleaner?: string;
  /** Ops's word: `planned`, `confirmed`, `in_progress`, `done`, `cancelled` or `no_access`. */
  readonly status?: string;
  /** What she may ask for about this visit, in the seam's request types. */
  readonly offers: readonly string[];
}

/**
 * What the office wrote for her about a visit done (ops #98, home #18 and #19): the note, and each
 * thing noticed on it, in ops's order and none where none came.
 */
export interface Journal {
  readonly whatWasDone?: string;
  readonly noticed: readonly Noticed[];
}

/**
 * Something noticed on a clean, as the office wrote it for her (ops #98 phase 3): its line, which a
 * screen draws as it came (`noticedLine`), and the seam's word for what it is, which none draws.
 */
export interface Noticed {
  readonly kind?: string;
  readonly summary: string;
}

/**
 * A home as a visit names it: its id, and its label as ops typed it. Never its address. A screen
 * names a visit's home by it only when she may see more than one home (`homeNameOf`).
 */
export interface HomeName {
  readonly id: string;
  readonly label?: string;
}

/** One home, in words, as the office wrote it down. Never a code. */
export interface HomeWrittenDown {
  readonly id: string;
  readonly label?: string;
  readonly bedrooms?: number;
  readonly bathrooms?: number;
  readonly doNotList?: string;
  readonly pets?: string;
  readonly entryNotes?: string;
  /** What she may ask for about this home: `home.edit` is a change to what is written down. */
  readonly offers: readonly string[];
  /** The edits of this home asked for, newest first: where each stands, never what it said. */
  readonly requests: readonly RequestShown[];
}

/** Home, from GET /api/home. */
export interface HomePlace {
  /** The business's day the answer was made on, so no screen reads the phone's clock for it. */
  readonly today: string;
  readonly household: HouseholdShown;
  /** Which sections have not come down from ops yet. */
  readonly arriving: {
    readonly household: boolean;
    readonly homes: boolean;
    readonly visits: boolean;
  };
  /**
   * More of the first fill may still be arriving: the household came down less than six hours ago
   * (provisional P106). Absent otherwise, and from a Worker before it.
   */
  readonly firstFill?: true;
  /** What she may ask for about the household as a whole. */
  readonly offers: readonly string[];
  /** What she would be offered but for the same ask already waiting on the office. */
  readonly held: readonly string[];
  readonly homes: readonly HomeWrittenDown[];
  readonly nextVisit: Visit | null;
  /**
   * The last clean at each home she may see, each with the cleaner's words when ops sent them,
   * newest day first, in the Worker's order: every one Home asks "How was it?" about. An answer
   * without the list, from a Worker before it, is read as none.
   */
  readonly lastCleans: readonly LastClean[];
}

/**
 * Her own newest answer about a clean, sent from home, as "How was it?" on the clean or "How did we
 * do?" on its invoice (P47): where it stands, and the face she chose, which the Worker sends to her
 * alone; never the area or the line, never anybody else's.
 */
export interface CleanAnswer {
  /** `sent`, `received`, `refused`, `stopped`, or a word Home says nothing of. */
  readonly state: string;
  /** The seam's word for her face, such as `so_so`. */
  readonly face?: string;
  /**
   * Whether ops recorded it, once its receipt said: false past the twelve answers from home ops
   * records on a clean, when the face she gave is not the one that counts.
   */
  readonly recorded?: boolean;
  /**
   * Ops's word for refusing it, where it was refused, such as `not_asked`: ops asked somebody else
   * about the clean (home #23), which the card says rather than that it did not go through.
   */
  readonly code?: string;
}

/**
 * A clean Home asks about: the visit, the cleaner's words, or null when none came, and her own
 * newest answer about it, when she has sent one from home (provisional P41): on the clean, or on its
 * invoice, which ops counts as her answer about the clean (P47), whichever she gave last.
 */
export interface LastClean {
  readonly visit: Visit;
  readonly journal: Journal | null;
  readonly answer?: CleanAnswer;
}

/** Asks GET /api/home for Home. */
export function getHome(): Promise<PlaceAnswer<HomePlace>> {
  return getPlace('/api/home', readHome);
}

/**
 * A place, read. 401 is signed out and 404 is no home to show; a 200 is the place only when its
 * reader can read it, and everything else, a failed request included, did not go through. Every
 * answer but a 200's is read for its status alone, and its body read to its end and let go
 * (`letGo`); but for a place that asks (`arriving`), an invoice's own page, a 404 is read for the
 * Worker's word that what was asked for may still be on its way (`notHereYet`).
 */
async function getPlace<T>(
  path: string,
  read: (body: unknown) => T | null,
  { arriving = false }: { readonly arriving?: boolean } = {},
): Promise<PlaceAnswer<T>> {
  try {
    const response = await fetch(path, { headers: { accept: 'application/json' } });
    if (response.status === 404 && arriving) return await notHereYet(response);
    if (response.status !== 200) {
      await letGo(response);
      if (response.status === 401) return { outcome: 'signed_out' };
      if (response.status === 404) return { outcome: 'no_home' };
      return { outcome: 'failed' };
    }
    const place = read(await response.json());
    return place === null ? { outcome: 'failed' } : { outcome: 'shown', place };
  } catch {
    return { outcome: 'failed' };
  }
}

/**
 * A 404 read for the Worker's word that what was asked for may still be on its way, `arriving:
 * true` (ops's plan, section 7, ask 5), and with it that a code opens it, `invited: true` (the review
 * of 7e33d61, finding 1), and nothing else of it; a body it cannot read is a 404 as ever.
 */
async function notHereYet(response: Response): Promise<PlaceAnswer<never>> {
  try {
    const body: unknown = await response.json();
    if (isFields(body) && body.arriving === true) {
      return body.invited === true
        ? { outcome: 'no_home', arriving: true, invited: true }
        : { outcome: 'no_home', arriving: true };
    }
  } catch {
    // The status has said what this answer is.
  }
  return { outcome: 'no_home' };
}

type Fields = Readonly<Record<string, unknown>>;

const isFields = (value: unknown): value is Fields =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const textOf = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const numberOf = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** A list of words, keeping only the words: anything else in it is dropped. */
const wordsOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/** Each item a reader can read, and none it cannot. */
function listOf<T>(value: unknown, read: (item: unknown) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const kept = read(item);
    return kept === null ? [] : [kept];
  });
}

/** An object of only the fields that are there, so an absent one is absent rather than undefined. */
function only<T extends object>(fields: T): T {
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined)) as T;
}

/** An invoice she can pay, read whole: its id, and a balance in whole cents above nothing. */
function readPayable(value: unknown): Payable | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  const balanceCents = numberOf(value.balanceCents);
  return balanceCents !== undefined && Number.isSafeInteger(balanceCents) && balanceCents > 0
    ? { id: value.id, balanceCents }
    : null;
}

/** A count the Worker gives, a whole number above nothing, or nothing to mark. */
const countOf = (value: unknown): number | undefined => {
  const count = numberOf(value);
  return count !== undefined && Number.isSafeInteger(count) && count > 0 ? count : undefined;
};

/** The household of a place, given the id its reader has already found to be one. */
function readHousehold(id: string, value: Fields): HouseholdShown {
  const payable = listOf(value.payable, readPayable);
  const onItsWay = listOf(value.onItsWay, readPayable);
  return only({
    id,
    name: textOf(value.name),
    payments: value.payments === true ? (true as const) : undefined,
    payable: payable.length > 0 ? payable : undefined,
    onItsWay: onItsWay.length > 0 ? onItsWay : undefined,
    owedUncounted: value.owedUncounted === true ? (true as const) : undefined,
    newMessages: countOf(value.newMessages),
    howWasIt: value.howWasIt === true ? (true as const) : undefined,
  });
}

function readVisit(value: unknown): Visit | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  const window = isFields(value.window) ? value.window : null;
  const start = textOf(window?.start);
  const end = textOf(window?.end);
  return only({
    id: value.id,
    homeId: textOf(value.homeId),
    date: textOf(value.date),
    window: start !== undefined && end !== undefined ? { start, end } : undefined,
    cleaner: textOf(value.cleaner),
    status: textOf(value.status),
    offers: wordsOf(value.offers),
  });
}

function readJournal(value: unknown): Journal | null {
  if (!isFields(value)) return null;
  return only({
    whatWasDone: textOf(value.whatWasDone),
    noticed: listOf(value.noticed, readNoticed),
  });
}

/** A thing noticed, or null where it has no words to draw, which would say nothing. */
function readNoticed(value: unknown): Noticed | null {
  if (!isFields(value)) return null;
  const summary = textOf(value.summary);
  if (summary === undefined || summary.trim() === '') return null;
  return only({ kind: textOf(value.kind), summary });
}

function readHomeName(value: unknown): HomeName | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  return only({ id: value.id, label: textOf(value.label) });
}

/** An answer of hers about a clean, with when she gave it and its id, to tell which is newer. */
interface Given {
  readonly at: number;
  readonly id: string;
  readonly answer: CleanAnswer;
}

/**
 * Her answer as Home reads it: where it stands, the face as a word, recorded as a flag, and ops's
 * word for a refusal as a word.
 */
const cleanAnswerOf = (
  state: string,
  face: unknown,
  recorded: unknown,
  code: unknown,
): CleanAnswer =>
  only({
    state,
    face: textOf(face),
    recorded: typeof recorded === 'boolean' ? recorded : undefined,
    code: textOf(code),
  });

/** When an answer was given, or the earliest of all where the Worker sent no instant to read. */
const givenAt = (value: unknown): number => {
  const at = typeof value === 'string' ? Date.parse(value) : Number.NaN;
  return Number.isNaN(at) ? Number.NEGATIVE_INFINITY : at;
};

/** The newer of two answers, by when she gave each and then by id, as the Worker orders hers. */
function newerOf(a: Given | undefined, b: Given | undefined): Given | undefined {
  if (a === undefined || b === undefined) return a ?? b;
  if (a.at !== b.at) return a.at > b.at ? a : b;
  return a.id > b.id ? a : b;
}

/**
 * Her own newest answer about a clean, from the visit as the Worker gives it and her answer on the
 * clean's invoice beside it, or none: read alike for Home's last clean and for a visit's own page,
 * which one visit's report is drawn from (home #21). Her answer on the clean is the first of the
 * visit's requests, which the Worker gives newest first, that is feedback of her own with a state;
 * of what she asked, only the face is read, and of ops's receipt, only whether it recorded it, as a
 * true or a false. Her answer on the clean's invoice (`invoiceAnswer`, provisional P47) is read the
 * same way, and only with a state; the newer of the two is her answer, since ops counts either as
 * her answer about the clean.
 */
function answerAbout(visitValue: unknown, invoiceAnswer: unknown): CleanAnswer | undefined {
  const requests = isFields(visitValue) ? visitValue.requests : undefined;
  const hers = Array.isArray(requests)
    ? requests.find(
        (each): each is Fields =>
          isFields(each) &&
          each.type === 'visit.feedback' &&
          each.mine === true &&
          typeof each.state === 'string',
      )
    : undefined;
  const asked = isFields(hers?.asked) ? hers.asked : {};
  const onClean: Given | undefined =
    hers === undefined
      ? undefined
      : {
          at: givenAt(hers.occurredAt),
          id: textOf(hers.id) ?? '',
          answer: cleanAnswerOf(String(hers.state), asked.face, hers.recorded, hers.code),
        };
  const onInvoice: Given | undefined =
    isFields(invoiceAnswer) && typeof invoiceAnswer.state === 'string'
      ? {
          at: givenAt(invoiceAnswer.occurredAt),
          id: textOf(invoiceAnswer.id) ?? '',
          answer: cleanAnswerOf(
            invoiceAnswer.state,
            invoiceAnswer.face,
            invoiceAnswer.recorded,
            invoiceAnswer.code,
          ),
        }
      : undefined;
  return newerOf(onClean, onInvoice)?.answer;
}

/**
 * A clean Home asks about, or null when its visit cannot be read, which is no clean to ask about,
 * with the cleaner's words and her own newest answer about it (`answerAbout`).
 */
function readLastClean(value: unknown): LastClean | null {
  if (!isFields(value)) return null;
  const visit = readVisit(value.visit);
  if (visit === null) return null;
  return only({
    visit,
    journal: readJournal(value.journal),
    answer: answerAbout(value.visit, value.invoiceAnswer),
  });
}

function readHomeWrittenDown(value: unknown): HomeWrittenDown | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  return only({
    id: value.id,
    label: textOf(value.label),
    bedrooms: numberOf(value.bedrooms),
    bathrooms: numberOf(value.bathrooms),
    doNotList: textOf(value.doNotList),
    pets: textOf(value.pets),
    entryNotes: textOf(value.entryNotes),
    offers: wordsOf(value.offers),
    requests: listOf(value.requests, readRequestShown),
  });
}

function readHome(body: unknown): HomePlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household, arriving } = body;
  if (!isFields(household) || typeof household.id !== 'string' || !isFields(arriving)) return null;
  return {
    today: body.today,
    household: readHousehold(household.id, household),
    arriving: {
      household: arriving.household === true,
      homes: arriving.homes === true,
      visits: arriving.visits === true,
    },
    ...(body.firstFill === true ? { firstFill: true as const } : {}),
    offers: wordsOf(body.offers),
    held: wordsOf(body.held),
    homes: listOf(body.homes, readHomeWrittenDown),
    nextVisit: readVisit(body.nextVisit),
    lastCleans: listOf(body.lastCleans, readLastClean),
  };
}

/** A line from the card added to a visit: its words, how many, and, to a client, its price. */
export interface Addon {
  readonly label?: string;
  readonly quantity?: number;
  readonly unitPriceCents?: number;
}

/**
 * A request about a visit, as the Worker shows it: where it stands, and who asked. Never what
 * somebody else wrote, which the Worker does not send; of what was asked, only the days a move
 * named, which the callout says when there is one.
 */
export interface RequestShown {
  readonly id: string;
  /** The seam's type, such as `visit.skip` or `visit.move`. */
  readonly type: string;
  /** Whether she asked for it herself. */
  readonly mine: boolean;
  /** Who asked, by the name the household lists, when it was not her. */
  readonly askedBy?: string;
  /** `sent`, `received`, `accepted`, `declined`, `cancelled`, `refused` or `stopped`. */
  readonly state: string;
  /** Why ops cancelled it, such as `withdrawn`. */
  readonly reason?: string;
  /** Ops's refusal code, such as `not_open`. */
  readonly code?: string;
  /** The office's own words, written or approved by a person there. */
  readonly answer?: string;
  /** The days a move said would suit, `YYYY-MM-DD`. */
  readonly daysThatSuit: readonly string[];
  /** The card's line an add-on asked to add, by its code (P112). */
  readonly rateLineCode?: string;
  /** How many of it an add-on asked for, 1 to 20 (P112). */
  readonly quantity?: number;
}

/**
 * An add-on line of the card in force that she may ask to add to a visit (provisional P112): its
 * code, the card's own words for it and its price as ops pushed it, and `from` where the card shows
 * that price as a starting price. Home holds no price of its own: each figure is the card's.
 */
export interface CardLine {
  readonly code: string;
  readonly label: string;
  readonly unitPriceCents: number;
  readonly from?: true;
}

/** A visit on Schedule, with what was added to it and the requests about it, newest first. */
export interface ScheduledVisit extends Visit {
  readonly addons: readonly Addon[];
  readonly requests: readonly RequestShown[];
}

/**
 * A home as Schedule's heading names it: its name, and its standing schedule's cadence and weekday
 * as ops's words, such as `every_2_weeks` and `tuesday`, which `words.ts` says; and since round six's
 * slice S3, a pause while it has not ended, as Account reads one (`RegularCleans`), and for a client
 * her household's asks about its regular cleans, as Account reads them, only where there is one,
 * an ending she asked as a cancel saying so itself (`CleansAskShown`'s `cancel`).
 */
export interface ScheduleHome extends RegularCleans {
  readonly requests?: readonly CleansAskShown[];
}

/** Schedule, from GET /api/schedule. */
export interface SchedulePlace {
  /** The business's day the answer was made on. */
  readonly today: string;
  readonly household: HouseholdShown;
  /** Whether the visits have not come down from ops yet. */
  readonly arriving: { readonly visits: boolean };
  /**
   * The card's add-on lines she may ask to add to an open visit (P112): none for a member, and none
   * from a card with no add-on line home can quote, or from a Worker that answers none.
   */
  readonly card: readonly CardLine[];
  /** Each home she may see, for the heading. */
  readonly homes: readonly ScheduleHome[];
  /** Every visit from today on, cancelled ones included: the List draws these. */
  readonly visits: readonly ScheduledVisit[];
  /**
   * Every visit before today the Worker reads back over, oldest first, for the month view. None
   * from a Worker that answers no `earlier`.
   */
  readonly earlier: readonly ScheduledVisit[];
}

/** Asks GET /api/schedule for Schedule. */
export function getSchedule(): Promise<PlaceAnswer<SchedulePlace>> {
  return getPlace('/api/schedule', readSchedule);
}

function readAddon(value: unknown): Addon | null {
  if (!isFields(value)) return null;
  return only({
    label: textOf(value.label),
    quantity: numberOf(value.quantity),
    unitPriceCents: numberOf(value.unitPriceCents),
  });
}

function readRequestShown(value: unknown): RequestShown | null {
  if (!isFields(value)) return null;
  const { id, type, mine, state } = value;
  if (typeof id !== 'string' || typeof type !== 'string' || typeof state !== 'string') return null;
  if (typeof mine !== 'boolean') return null;
  const asked = isFields(value.asked) ? value.asked : {};
  return only({
    id,
    type,
    mine,
    askedBy: textOf(value.askedBy),
    state,
    reason: textOf(value.reason),
    code: textOf(value.code),
    answer: textOf(value.answer),
    daysThatSuit: wordsOf(asked.daysThatSuit),
    rateLineCode: textOf(asked.rateLineCode),
    quantity: numberOf(asked.quantity),
  });
}

/** An add-on line of the card, read whole: its code, its words and a price, or not at all. */
function readCardLine(value: unknown): CardLine | null {
  if (!isFields(value)) return null;
  const { code, label } = value;
  const unitPriceCents = numberOf(value.unitPriceCents);
  if (typeof code !== 'string' || typeof label !== 'string' || unitPriceCents === undefined) {
    return null;
  }
  return value.from === true
    ? { code, label, unitPriceCents, from: true }
    : { code, label, unitPriceCents };
}

function readScheduledVisit(value: unknown): ScheduledVisit | null {
  const visit = readVisit(value);
  if (visit === null || !isFields(value)) return null;
  return {
    ...visit,
    addons: listOf(value.addons, readAddon),
    requests: listOf(value.requests, readRequestShown),
  };
}

function readScheduleHome(value: unknown): ScheduleHome | null {
  const home = readRegularCleans(value);
  if (home === null || !isFields(value)) return null;
  const requests = listOf(value.requests, readCleansAsk);
  return requests.length > 0 ? { ...home, requests } : home;
}

/** Schedule. An answer with no homes, from a Worker before the heading, is read as none. */
function readSchedule(body: unknown): SchedulePlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household, arriving } = body;
  if (!isFields(household) || typeof household.id !== 'string' || !isFields(arriving)) return null;
  return {
    today: body.today,
    household: readHousehold(household.id, household),
    arriving: { visits: arriving.visits === true },
    card: listOf(body.card, readCardLine),
    homes: listOf(body.homes, readScheduleHome),
    visits: listOf(body.visits, readScheduledVisit),
    earlier: listOf(body.earlier, readScheduledVisit),
  };
}

/**
 * A visit's own page, from GET /api/visits/:id: the visit, the household it is on, which a request
 * about it names, and each home she may see, which names the visit's home among several. Feedback
 * and one visit's report (home #21) are drawn from it. Its journal is read as Home reads a last
 * clean's, the note and each thing noticed, and with them its photos (`VisitJournal`); her own
 * newest answer about the clean as Home reads it (`answerAbout`); and what the visit holds but for
 * the same ask already waiting on the office (`held`): the report draws them. Its price and every
 * other request are not read: no page drawn from it draws them.
 */
export interface VisitPlace {
  /** The business's day the answer was made on. */
  readonly today: string;
  readonly household: HouseholdShown;
  readonly visit: Visit;
  /** What the office wrote for her about the visit, or null where it has written nothing yet. */
  readonly journal: VisitJournal | null;
  /** Her own newest answer about the clean from home, on it or on its invoice, or none (P47). */
  readonly answer?: CleanAnswer;
  /**
   * What she would be offered about the visit but for the same ask already waiting on the office,
   * in the seam's request types: `journal.photos` while an ask for its photos again waits, by the
   * request door's own hold (home #21). An answer without it is read as nothing held.
   */
  readonly held: readonly string[];
  /** Each home she may see, by its label. An answer without them is read as none. */
  readonly homes: readonly HomeName[];
}

/**
 * Where a photo of a visit stands on home's server (home #21): `held`, drawn from home's own address
 * (`photoFile`); `coming`, while home reads it from ops, which a read of the page again a moment
 * later draws; or `failed`, a read that failed just now, which an open of the page later tries again.
 */
export type PhotoState = 'held' | 'coming' | 'failed';

const PHOTO_STATES: readonly PhotoState[] = ['held', 'coming', 'failed'];

/**
 * One photo of a visit's journal, as its own page lists it (home #21): ops's id, which names it in
 * home's own address for it, the household's own line it names, and where it stands. Never a link
 * of ops's, which home's server alone reads, and never where home keeps it.
 */
export interface VisitPhoto {
  readonly id: string;
  readonly caption?: string;
  readonly state: PhotoState;
}

/**
 * A visit's journal as its own page reads it (home #21): Home's and Messages' journal, with the
 * photos to draw in the Worker's order; when the first of their links runs out, an instant, absent
 * where none says; whether a link of the journal has run out (`photosExpired`); and whether every
 * photo it had is no longer on her page, hidden or gone (`photosGone`): either way she may ask for
 * them again (`journal.photos`). Home's and Messages' journals carry no photos.
 */
export interface VisitJournal extends Journal {
  readonly photos: readonly VisitPhoto[];
  readonly photosUntil?: string;
  readonly photosExpired: boolean;
  readonly photosGone: boolean;
}

/** Her ask for a visit's photos again, as the seam types it (`journal.photos`). */
export const PHOTOS_AGAIN = 'journal.photos';

/**
 * HOME'S OWN ADDRESS FOR ONE PHOTO OF A VISIT (home #21): `GET /api/visits/:visitId/photos/:photoId`,
 * on the household the visit's page was read with, which is the household that holds the visit:
 * the photo's own address names it, since with none named the Worker reads the first household of
 * hers ops has granted, which need not be that one; each part encoded, so it names one visit and
 * one photo and never another path. The only address a photo is ever drawn from: never one of ops's.
 */
export function photoFile(visitId: string, photoId: string, household: string): string {
  const query = new URLSearchParams({ household });
  return `/api/visits/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}?${query}`;
}

/** An instant as the Worker writes one, ISO 8601 with its zone. */
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** An instant, kept as it came, or nothing for anything else. */
const instantOf = (value: unknown): string | undefined =>
  typeof value === 'string' && INSTANT.test(value) && !Number.isNaN(Date.parse(value))
    ? value
    : undefined;

/**
 * A photo the page can draw, or null: one with no id in words, or a state this build does not know,
 * is no photo here. Its line is kept only where it is words. Nothing else of it is read.
 */
function readPhoto(value: unknown): VisitPhoto | null {
  if (!isFields(value)) return null;
  const { id } = value;
  const state = PHOTO_STATES.find((each) => each === value.state);
  if (typeof id !== 'string' || id === '' || state === undefined) return null;
  return only({ id, caption: wordsIn(value.caption), state });
}

/**
 * A visit's journal with its photos, the first of each id in the Worker's order; when they may be
 * seen until only as an instant, and whether one has expired, and whether they are gone, each only
 * as a true.
 */
function readVisitJournal(value: unknown): VisitJournal | null {
  const journal = readJournal(value);
  if (journal === null || !isFields(value)) return null;
  const named = new Set<string>();
  const photos = listOf(value.photos, readPhoto).filter((photo) => {
    if (named.has(photo.id)) return false;
    named.add(photo.id);
    return true;
  });
  return only({
    ...journal,
    photos,
    photosUntil: instantOf(value.photosUntil),
    photosExpired: value.photosExpired === true,
    photosGone: value.photosGone === true,
  });
}

/**
 * Asks GET /api/visits/:id for one visit. The id is encoded as the path's last part, so whatever
 * it holds names one visit and never another path. It names no household, as the visit-done email's
 * link names none: the Worker finds the visit in whichever granted household of hers holds it. A
 * 404 is a visit the Worker does not find for her, which is also how a visit at a home that is not
 * hers reads.
 */
export function getVisit(id: string): Promise<PlaceAnswer<VisitPlace>> {
  return getPlace(`/api/visits/${encodeURIComponent(id)}`, readVisitPlace);
}

function readVisitPlace(body: unknown): VisitPlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household } = body;
  if (!isFields(household) || typeof household.id !== 'string') return null;
  const visit = readVisit(body.visit);
  if (visit === null) return null;
  return only({
    today: body.today,
    household: readHousehold(household.id, household),
    visit,
    journal: readVisitJournal(body.journal),
    answer: answerAbout(body.visit, body.invoiceAnswer),
    held: wordsOf(isFields(body.visit) ? body.visit.held : undefined),
    homes: listOf(body.homes, readHomeName),
  });
}

/** A message she wrote to the office: where it stands, the office's answer, and her own words. */
export interface MessageRequest extends RequestShown {
  /** What she wrote, exactly as she typed it. */
  readonly body?: string;
}

/** Where ops last said a copy of a message stands with the phone system, in ops's own words. */
export type MessageState = 'handed_off' | 'delivered' | 'failed';

const MESSAGE_STATES: readonly MessageState[] = ['handed_off', 'delivered', 'failed'];

/**
 * One item of her Messages thread, on the business's day it belongs to:
 *
 * - `journal`, the "Your home is ready" card of a visit done, with its home and the cleaner's
 *   first name;
 * - `message`, a copy of a message the office sent her, with where ops last said its delivery
 *   stands: a text's, or an email's, which says so (`email`, slice M2);
 * - `request`, a message she wrote to the office, with the office's answer once one has come.
 */
export type ThreadItem =
  | {
      readonly kind: 'journal';
      readonly day: string;
      readonly visit: { readonly id: string; readonly homeId?: string; readonly cleaner?: string };
      readonly journal: Journal;
    }
  | {
      readonly kind: 'message';
      readonly day: string;
      readonly message: {
        readonly id: string;
        readonly body: string;
        /**
         * Ops's word for where its delivery stands, as last pushed, and absent for any other word:
         * only `failed` is drawn, so no screen says a message was delivered.
         */
        readonly state?: MessageState;
        /**
         * The first name of the person at the office who wrote or approved it, absent for one the
         * system composed.
         */
        readonly author?: string;
        /**
         * The invoice the copy carries ops's link to, where her copy holds it (round six, slice
         * S3): the link drawn for ops's words goes to that invoice's own page.
         */
        readonly invoiceId?: string;
        /**
         * The clean the copy is about, where Schedule draws it (slice M1): "See this clean" under
         * ops's words leads to it.
         */
        readonly visitId?: string;
        /** The home it is about, where it is one of hers, which names it among several. */
        readonly homeId?: string;
        /**
         * On the copy of an email ops composed and home delivered to her, once Microsoft took it
         * (slice M2, ops's section 11.1), and on nothing else: its subject, as ops wrote it, and
         * the address it went to as ops cut it, `k@example.com`, each absent where none came.
         */
        readonly email?: EmailSaid;
      };
    }
  | { readonly kind: 'request'; readonly day: string; readonly request: MessageRequest };

/** What the copy of an email says of itself: its subject, and the address it went to, cut down. */
export interface EmailSaid {
  readonly subject?: string;
  readonly toMasked?: string;
}

/** The Worker's word for the copy of an email: the only channel a copy says it came by. */
const EMAIL_CHANNEL = 'email';

/** Words, kept as they came, or nothing for anything else, or words that are only spaces. */
const wordsIn = (value: unknown): string | undefined => {
  const text = textOf(value);
  return text !== undefined && text.trim() !== '' ? text : undefined;
};

/** Messages, from GET /api/messages. */
export interface MessagesPlace {
  /** The business's day the answer was made on. */
  readonly today: string;
  readonly household: HouseholdShown;
  /** Whether the household has not come down from ops yet, so neither has anything to say. */
  readonly arriving: { readonly messages: boolean };
  /** What she may ask for about the household: `message` is writing to the office. */
  readonly offers: readonly string[];
  /**
   * What she would be offered but for the same ask already waiting on the office: `message` while
   * the household's last message is still open, whoever on it wrote it.
   */
  readonly held: readonly string[];
  /** Each home she may see, by its label. An answer without them is read as none. */
  readonly homes: readonly HomeName[];
  /** Oldest first, in the Worker's order. */
  readonly thread: readonly ThreadItem[];
  /**
   * The newest thing the office said to her in the thread, an instant, absent when it has said
   * nothing: what the page posts once drawn as what she has seen (`markMessagesSeen`, P49).
   */
  readonly newestFromOffice?: string;
}

/** Asks GET /api/messages for Messages. */
export function getMessages(): Promise<PlaceAnswer<MessagesPlace>> {
  return getPlace('/api/messages', readMessages);
}

/**
 * An item of the thread, or null when it cannot be read whole: a kind this build does not draw, an
 * item with no day, or a message with no words, which would say nothing. A message's author that is
 * not a name, or is blank, is left out, so the message is signed by the business alone, and its
 * state is kept only when it is one of ops's three words. A copy that says it is an email's
 * (`channel` `email`) carries its subject and the address it went to, each only where it is words;
 * any other copy carries neither, whatever came with it.
 */
function readThreadItem(value: unknown): ThreadItem | null {
  if (!isFields(value) || typeof value.day !== 'string') return null;
  const { day } = value;
  switch (value.kind) {
    case 'journal': {
      const visit = isFields(value.visit) ? value.visit : null;
      const journal = readJournal(value.journal);
      if (visit === null || typeof visit.id !== 'string' || journal === null) return null;
      return {
        kind: 'journal',
        day,
        visit: only({ id: visit.id, homeId: textOf(visit.homeId), cleaner: textOf(visit.cleaner) }),
        journal,
      };
    }
    case 'message': {
      const message = isFields(value.message) ? value.message : null;
      if (message === null || typeof message.id !== 'string') return null;
      if (typeof message.body !== 'string') return null;
      const author = textOf(message.author)?.trim();
      const state = MESSAGE_STATES.find((word) => word === message.state);
      // What it is about: an id ops minted, or nothing for anything else, a blank one included.
      const about = (value: unknown) => textOf(value) || undefined;
      return {
        kind: 'message',
        day,
        message: only({
          id: message.id,
          body: message.body,
          state,
          author: author || undefined,
          invoiceId: about(message.invoiceId),
          visitId: about(message.visitId),
          homeId: about(message.homeId),
          email:
            message.channel === EMAIL_CHANNEL
              ? only({ subject: wordsIn(message.subject), toMasked: wordsIn(message.toMasked) })
              : undefined,
        }),
      };
    }
    case 'request': {
      const request = readRequestShown(value.request);
      if (request === null || !isFields(value.request)) return null;
      const asked = isFields(value.request.asked) ? value.request.asked : {};
      return { kind: 'request', day, request: only({ ...request, body: textOf(asked.body) }) };
    }
    default:
      return null;
  }
}

function readMessages(body: unknown): MessagesPlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household, arriving } = body;
  if (!isFields(household) || typeof household.id !== 'string' || !isFields(arriving)) return null;
  const newest = textOf(body.newestFromOffice);
  return only({
    today: body.today,
    household: readHousehold(household.id, household),
    arriving: { messages: arriving.messages === true },
    offers: wordsOf(body.offers),
    held: wordsOf(body.held),
    homes: listOf(body.homes, readHomeName),
    thread: listOf(body.thread, readThreadItem),
    newestFromOffice: newest === '' ? undefined : newest,
  });
}

/**
 * Says she has seen what the office said on Messages, up to `through`, the newest thing the page
 * was given (`newestFromOffice`), once the page has drawn it (provisional P49): POST
 * /api/messages/seen, which the Worker keeps for her so the nav counts only what the office says
 * after. Nothing waits on it and nothing is said of it: one that did not go through leaves the count
 * as it was, to clear the next time she opens Messages. Its answer is read to its end and let go.
 */
export async function markMessagesSeen(household: string, through: string): Promise<void> {
  try {
    await letGo(await postJson('/api/messages/seen', { household, through }));
  } catch {
    // Not kept: the count stays until the next time she opens Messages.
  }
}

/*
 * PAYMENTS, a client's alone: what is owed and what is paid, as ops last pushed it. Every amount,
 * state and flag is ops's, and the screen works out none of its own (CLAUDE.md binding rule 1).
 */

/** One line of an invoice: the office's words for it and what it comes to, either sign. */
export interface InvoiceLine {
  /** Absent when the Worker's words test held it back; the amount still stands. */
  readonly description?: string;
  readonly amountCents?: number;
}

/**
 * What there is to pay on an invoice from home now, as ops reads it, whether the bank and card
 * screens exist at all, and Zelle's details: with neither a way, its own page gives only the
 * office's number, and its card on Payments offers no Pay. A flag the Worker did not send as true is
 * false, which offers nothing and no screen ops did not say exists.
 */
export interface InvoicePay {
  /** There is something to pay from home now. */
  readonly payable: boolean;
  /** A bank payment on it is on its way, so there is nothing to pay while it clears. */
  readonly clearing: boolean;
  /** The bank and card screens exist: Stripe is on at ops. */
  readonly online: boolean;
  /**
   * Ops offers to save a method for the household, which says nothing is saved (the seam, l.352),
   * and which draws the bank's own tick (section 5 at 0b51e49, "Her tick"). Read on Payments too, so
   * How you pay leads her to the newest invoice that offers it (slice A2).
   */
  readonly offerToSave: boolean;
  readonly zelle?: Zelle;
}

/**
 * The household's saved method while it pays the invoice: Stripe's words for it and its type, from
 * which ops's words for it are made (`savedMethodWords`), and the stage.
 */
export interface InvoiceAutopay {
  readonly label?: string;
  /** `card`, `us_bank_account` or `link`; absent for a type ops does not know. */
  readonly methodType?: string;
  /** `waiting`, `paying_by_zelle`, `pause_lifted` or `at_stripe`. */
  readonly stage?: string;
}

/** The automatic charge that paid an invoice. */
export interface Charged {
  /** Stripe's words for the method, such as `Visa 4242`, which ops's words for it are made from. */
  readonly label?: string;
  /** `card`, `us_bank_account` or `link`; absent for a type ops does not know. */
  readonly methodType?: string;
  /** The business's day it was charged on, `YYYY-MM-DD`. */
  readonly chargedOn?: string;
  /** Ops emailed a receipt. */
  readonly receiptEmailed: boolean;
}

/**
 * A payment applied to an invoice, as ops recorded it (ops's section 11.2, round five's slice B3):
 * `payment`, `refund` or `correction`; how it came, ops's own word, `card`, `bank_transfer`, `zelle`,
 * `check`, `cash` or `other`, which home words; the day the money arrived, `YYYY-MM-DD`; the amount
 * applied here, a refund's below nothing; its receipt among the invoice's documents; and whether
 * ops's receipt email for it went, true only where ops says so. Ops's word that money arrived, never
 * home's (CLAUDE.md binding rule 1).
 */
export interface InvoicePayment {
  readonly id: string;
  readonly kind?: string;
  readonly method?: string;
  readonly receivedOn?: string;
  readonly amountCents?: number;
  readonly receiptDocumentId?: string;
  readonly receiptEmailed: boolean;
}

/**
 * One of an invoice's PDFs (ops's section 11.2): its id, which her press names; `invoice`, `receipt`
 * or `cancellation`, or a kind ops adds later, which offers nothing; the number it carries, the
 * invoice's for an invoice and a cancellation, the receipt's own for a receipt; the day it was
 * issued; whether ops says it is made, true only where it says so; and the payment a receipt is for.
 */
export interface InvoiceDocument {
  readonly id: string;
  readonly kind?: string;
  readonly number?: string;
  readonly issuedOn?: string;
  readonly ready: boolean;
  readonly paymentId?: string;
}

/** An invoice as Payments draws it. */
export interface Invoice {
  readonly id: string;
  /**
   * Its number, `FK-42`, as ops gives it at its first send (ops's section 11.2), which tells two
   * invoices of one day apart; absent for one sent before numbering began.
   */
  readonly number?: string;
  /**
   * Ops's site id for the home of its clean, absent when its visit is not in the copy: it names the
   * invoice among several homes by the home's label, as a visit is named (`homeNameOf`, P45).
   */
  readonly homeId?: string;
  /** The clean's day, `YYYY-MM-DD`, absent when ops could not read the visit. */
  readonly visitDate?: string;
  /** The day it was issued, `YYYY-MM-DD`. */
  readonly issuedOn?: string;
  /** The day it is due, `YYYY-MM-DD`. */
  readonly dueOn?: string;
  /** `sent`, `part_paid`, `paid`, `overdue`, `void` or `written_off`, or a word ops adds later. */
  readonly state?: string;
  readonly totalCents?: number;
  /** What is still to pay, as ops reads it. */
  readonly balanceCents?: number;
  readonly lines: readonly InvoiceLine[];
  readonly pay: InvoicePay;
  /** Present while the household's saved method pays it. */
  readonly autopay?: InvoiceAutopay;
  /** Present once an automatic charge paid it. */
  readonly charged?: Charged;
  /** The face that counts on it, as ops last pushed it: the seam's word, such as `so_so`. */
  readonly latestFace?: string;
  /**
   * Who is asked "How did we do?", for her, as the Worker says it (home #23, ops #86 part 3):
   * `not_asked`, ops asked somebody else, so the page draws neither the faces nor the thanks; or
   * `with_others`, she is asked beside somebody else, so the face that counts may be theirs and only
   * her own answer lights one. Absent where the faces are as before.
   */
  readonly asked?: 'with_others' | 'not_asked';
  /**
   * Her report, or anybody's on the household, that it was sent by Zelle, as the Worker carries it
   * (ops's section 11.7, P53): on its way, so its Pay is held back on every device; or, on its own
   * page alone, her own report refused or given up on. Absent otherwise.
   */
  readonly zelleReport?: ZelleReport;
  /** The payments applied to it, in the order the money arrived, absent where ops sent none. */
  readonly payments?: readonly InvoicePayment[];
  /** Its PDFs, in the order ops made them, absent where ops sent none. */
  readonly documents?: readonly InvoiceDocument[];
}

/** A payment read whole: its id, and each other field only in the type it should have. */
function readInvoicePayment(value: unknown): InvoicePayment | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  return only({
    id: value.id,
    kind: textOf(value.kind),
    method: textOf(value.method),
    receivedOn: textOf(value.receivedOn),
    amountCents: numberOf(value.amountCents),
    receiptDocumentId: textOf(value.receiptDocumentId),
    receiptEmailed: value.receiptEmailed === true,
  });
}

/** A document read whole: its id, made only as true, and each other field in the type it should have. */
function readInvoiceDocument(value: unknown): InvoiceDocument | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  return only({
    id: value.id,
    kind: textOf(value.kind),
    number: textOf(value.number),
    issuedOn: textOf(value.issuedOn),
    ready: value.ready === true,
    paymentId: textOf(value.paymentId),
  });
}

/** A list read with `read`, or nothing where none of it can be read. */
function someOf<T>(value: unknown, read: (item: unknown) => T | null): T[] | undefined {
  const list = listOf(value, read);
  return list.length > 0 ? list : undefined;
}

/**
 * HER REPORT THAT SHE SENT AN INVOICE BY ZELLE, as the Worker carries it (ops's section 11.7, round
 * five's slice B2, P53):
 *
 * - `on_its_way`: the invoice is held as on its way, with no Pay. `sentOn` is the business's day she
 *   said she sent it, `YYYY-MM-DD`, as she picked it; `own` whether her own person reported it,
 *   never who else did, and both absent where the Worker holds no report of its own; `sending`
 *   while ops has not answered it yet.
 * - `refused`: her own newest report, refused by ops, with its code, until ops's push says how the
 *   invoice stands.
 * - `failed`: her own newest report, which home gave up sending.
 * - `taken_back` (11.7, "Taking it back"): she took her own report back and nothing holds the
 *   invoice on its way, so every way to pay is offered: `sending` while ops has not answered her,
 *   `code` where ops refused it saying no report stands, neither once ops took it back. But `code`
 *   `invoice_not_open`, carried only until the push catches up with it, says the invoice owes
 *   nothing now, so nothing is offered to pay on it (`owesNothingSaid`, `lib/invoices.ts`).
 * - On `on_its_way`, `takeBack`: what came of her taking it back while the invoice is still held,
 *   for the person who made the report alone (`TakeBack`).
 *
 * Never read as paid (CLAUDE.md binding rule 1).
 */
export type ZelleReport =
  | {
      readonly state: 'on_its_way';
      readonly sentOn?: string;
      readonly own?: boolean;
      readonly sending?: true;
      readonly takeBack?: TakeBack;
    }
  | { readonly state: 'refused'; readonly code?: string }
  | { readonly state: 'failed' }
  | { readonly state: 'taken_back'; readonly sending?: true; readonly code?: string };

/**
 * What came of her taking back a report that still holds the invoice on its way: `sending` while
 * ops has not answered it; `refused`, with ops's code, where a report may still stand, another
 * person's among them (`reported_by_another`); `failed` where home gave up sending it.
 */
export type TakeBack =
  | { readonly state: 'sending' }
  | { readonly state: 'refused'; readonly code?: string }
  | { readonly state: 'failed' };

/** What came of her taking it back, read only whole. */
function readTakeBack(value: unknown): TakeBack | undefined {
  if (!isFields(value)) return undefined;
  switch (value.state) {
    case 'sending':
      return { state: 'sending' };
    case 'refused':
      return only({ state: 'refused' as const, code: textOf(value.code) });
    case 'failed':
      return { state: 'failed' };
    default:
      return undefined;
  }
}

/** A report as the Worker carries it, read only whole: a day as text, a flag only as one. */
function readZelleReport(value: unknown): ZelleReport | undefined {
  if (!isFields(value)) return undefined;
  switch (value.state) {
    case 'on_its_way':
      return only({
        state: 'on_its_way' as const,
        sentOn: textOf(value.sentOn),
        own: typeof value.own === 'boolean' ? value.own : undefined,
        sending: value.sending === true ? (true as const) : undefined,
        takeBack: readTakeBack(value.takeBack),
      });
    case 'refused':
      return only({ state: 'refused' as const, code: textOf(value.code) });
    case 'failed':
      return { state: 'failed' };
    case 'taken_back':
      return only({
        state: 'taken_back' as const,
        sending: value.sending === true ? (true as const) : undefined,
        code: textOf(value.code),
      });
    default:
      return undefined;
  }
}

/** The method that charges automatically for the household. */
export interface SavedMethod {
  /** Stripe's words for it, such as `Visa 4242`, which ops's words for it are made from. */
  readonly label?: string;
  /** `card`, `us_bank_account` or `link`; absent for a type ops does not know. */
  readonly methodType?: string;
  /**
   * The instant she agreed, never drawn: it tells one method from the next, so a Stop sent about
   * one is never said of another saved after it (`screens/Payments.tsx`).
   */
  readonly since?: string;
  /** The business's day she agreed on, `YYYY-MM-DD`. */
  readonly agreedOn?: string;
  /**
   * The words ops holds for the version she agreed under (12.1, `terms_words`), exactly as the
   * Worker gives them, which How you pay says how the method is charged in (round six, slice S4).
   * GET /api/payments alone carries them.
   */
  readonly words?: string;
}

/**
 * Where her newest Stop of the saved method stands, as the Worker reads it from what ops answered
 * (the seam, l.540): not yet with ops (`sending`), stopped, nothing standing (`not_stopped`), with
 * ops but not said (`sent`), refused with ops's code, or given up on (`failed`). The Worker gives
 * it only while the method it was about still stands.
 */
export type StopAsk =
  | {
      readonly id: string;
      readonly state: 'sending' | 'stopped' | 'not_stopped' | 'sent' | 'failed';
    }
  | { readonly id: string; readonly state: 'refused'; readonly code?: string };

/** Payments, from GET /api/payments. */
export interface PaymentsPlace {
  /** The business's day the answer was made on. */
  readonly today: string;
  readonly household: HouseholdShown;
  /** Whether the invoices have not come down from ops yet. */
  readonly arriving: { readonly invoices: boolean };
  /** The household's saved method while one stands, else null. */
  readonly autopay: SavedMethod | null;
  /** Her newest Stop of the saved method that stands, when she has asked one. */
  readonly stopAsk?: StopAsk;
  /**
   * How you pay may offer to save a way to pay with nothing owed (ops's section 12.2, round five,
   * slice A5): the Worker says so only while its var says ops's door for it is open on its host,
   * nothing is saved and she is a granted client. Read only as true; absent otherwise.
   */
  readonly saveOffered?: true;
  /**
   * Each home she may see, by its label, which names an invoice's home among several. An answer
   * without them is read as none.
   */
  readonly homes: readonly HomeName[];
  /** `sent`, `part_paid` and `overdue`, newest first, in the Worker's order. */
  readonly toPay: readonly Invoice[];
  /** `paid`, newest first, in the Worker's order. */
  readonly paid: readonly Invoice[];
}

/** Asks GET /api/payments for Payments. A 404 is a person who is shown no money, or no household. */
export function getPayments(): Promise<PlaceAnswer<PaymentsPlace>> {
  return getPlace('/api/payments', readPayments);
}

function readInvoiceLine(value: unknown): InvoiceLine | null {
  if (!isFields(value)) return null;
  return only({ description: textOf(value.description), amountCents: numberOf(value.amountCents) });
}

/** The Worker's two words for who is asked "How did we do?" where the faces are not as before. */
const ASKED = ['with_others', 'not_asked'] as const;

function readInvoice(value: unknown): Invoice | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  const pay = isFields(value.pay) ? value.pay : {};
  const autopay = isFields(value.autopay) ? value.autopay : null;
  const charged = isFields(value.charged) ? value.charged : null;
  const feedback = isFields(value.feedback) ? value.feedback : {};
  const number = textOf(value.number);
  return only({
    id: value.id,
    number: number === undefined || number.trim() === '' ? undefined : number,
    homeId: textOf(value.homeId),
    visitDate: textOf(value.visitDate),
    issuedOn: textOf(value.issuedOn),
    dueOn: textOf(value.dueOn),
    state: textOf(value.state),
    totalCents: numberOf(value.totalCents),
    balanceCents: numberOf(value.balanceCents),
    lines: listOf(value.lines, readInvoiceLine),
    pay: only({
      payable: pay.payable === true,
      clearing: pay.clearing === true,
      online: pay.online === true,
      offerToSave: pay.offerToSave === true,
      zelle: readZelle(pay.zelle),
    }),
    autopay:
      autopay === null
        ? undefined
        : only({
            label: textOf(autopay.label),
            methodType: textOf(autopay.methodType),
            stage: textOf(autopay.stage),
          }),
    charged:
      charged === null
        ? undefined
        : only({
            label: textOf(charged.label),
            methodType: textOf(charged.methodType),
            chargedOn: textOf(charged.chargedOn),
            receiptEmailed: charged.receiptEmailed === true,
          }),
    latestFace: textOf(feedback.latestFace),
    asked: ASKED.find((word) => word === feedback.asked),
    zelleReport: readZelleReport(value.zelleReport),
    payments: someOf(value.payments, readInvoicePayment),
    documents: someOf(value.documents, readInvoiceDocument),
  });
}

const STOP_STATES = ['sending', 'stopped', 'not_stopped', 'sent', 'failed'] as const;

function readStopAsk(value: unknown): StopAsk | undefined {
  if (!isFields(value) || typeof value.id !== 'string') return undefined;
  const { id, state } = value;
  if (state === 'refused') return only({ id, state: 'refused' as const, code: textOf(value.code) });
  const known = STOP_STATES.find((each) => each === state);
  return known === undefined ? undefined : { id, state: known };
}

function readSavedMethod(value: unknown): SavedMethod | null {
  if (!isFields(value)) return null;
  return only({
    label: textOf(value.label),
    methodType: textOf(value.methodType),
    since: textOf(value.since),
    agreedOn: textOf(value.agreedOn),
    words: textOf(value.words),
  });
}

/**
 * Her newest answer about an invoice's clean, given on the invoice as "How did we do?" or on its
 * clean as "How was it?", which ops counts on the invoice: its id, and whether ops recorded it, once
 * ops's receipt has said. Ops records nothing past twelve answers from home on an invoice.
 * Provisional P47: the face she gave, while it went through or is on its way and ops has not said it
 * recorded nothing of it; and `afterPush` while ops's last push of the invoice cannot carry it yet,
 * because it waits to be sent or ops had it only after that push, so the page lights hers until a
 * push catches up.
 */
export interface FeedbackAsk {
  /** The request's id, the one the door answered when it was sent. */
  readonly id: string;
  readonly recorded?: boolean;
  /** The seam's word for her face, such as `so_so`. */
  readonly face?: string;
  readonly afterPush?: true;
  /** Ops refused it because it asked somebody else about the clean (home #23). Only as true. */
  readonly notAsked?: true;
}

/**
 * The business's Zelle details, as ops pushes them on the invoice (the seam, l.353 at b727ec1).
 * Home hardcodes none of it, and there is no Zelle without a tag to send to.
 */
export interface Zelle {
  /** What she types in her bank's Zelle: the business's tag. */
  readonly tag: string;
  /**
   * The address the code encodes, https only, which the code is drawn from and "Open Zelle's page"
   * opens (the seam, section 5 at 0b51e49, Q4; `components/ZelleCode.tsx`).
   */
  readonly qrUrl?: string;
  /** The name her bank shows for the tag. */
  readonly name?: string;
  /** What she writes as the memo, such as `FK Oct 13`. */
  readonly memo?: string;
}

/**
 * The business's hours as ops pushes them (the seam, l.356 at b727ec1): `start` and `end` as
 * `HH:MM` in Florida, the start included and the end not, and `days` the weekdays as JavaScript
 * numbers them, 0 being Sunday. Read only whole.
 */
export interface BusinessHours {
  readonly start: string;
  readonly end: string;
  readonly days: readonly number[];
}

/**
 * The household's saved method while it pays the invoice, as its own page reads it: what Payments
 * reads, and besides it the business's hours it is charged in, and whether a receipt is emailed
 * when it is (the seam, l.356).
 */
export interface PageAutopay extends InvoiceAutopay {
  readonly hours?: BusinessHours;
  /** A receipt is emailed when it is charged: only where ops says so. */
  readonly receiptByEmail: boolean;
}

/**
 * Where her newest "I'm paying by Zelle" on the invoice stands, as the Worker reads it from what ops
 * answered (the seam, l.539), carried only while the push has not yet said what came of it: not yet
 * with ops (`sending`), with ops with the stage its receipt said or none (`sent`), refused with
 * ops's code, or given up on (`failed`).
 */
export type ZelleAsk =
  | { readonly id: string; readonly state: 'sending' | 'failed' }
  | { readonly id: string; readonly state: 'sent'; readonly stage?: string }
  | { readonly id: string; readonly state: 'refused'; readonly code?: string };

/**
 * An invoice as its own page draws it: what Payments draws, the ways to pay it and the face that
 * counts on it among them, and besides it the state ops says it is in, the day it is due, what the
 * saved method charged, her newest answer on it, and the saved method paying it with her newest
 * "I'm paying by Zelle".
 */
/**
 * What there is to pay from home now, as an invoice's own page reads it: what Payments reads, whether
 * ops offers to save a method for the household among it, true only where ops says so. The card is
 * asked for behind a fresh code whatever it says, since ops decides Stripe's box on the card page
 * from its own read, and this push can lag it (`components/StripeWay.tsx`).
 */
export type PagePay = InvoicePay;

/**
 * Her newest ask to pay the invoice, as its page reads it: only a refusal, with ops's word for it,
 * which the Worker carries while ops's push has not caught up with it (`payAskCarried`), so the
 * ways to pay hold what ops refused across a reload. Every other state is the poll's to say.
 */
export interface PayAsk {
  readonly state: 'refused';
  readonly code?: string;
}

export interface InvoicePage extends Invoice {
  readonly pay: PagePay;
  readonly autopay?: PageAutopay;
  readonly zelleAsk?: ZelleAsk;
  readonly payAsk?: PayAsk;
  readonly charged?: Charged & { readonly amountCents?: number };
  readonly feedbackAsk?: FeedbackAsk;
  /**
   * Ops has said, since it read the invoice, that the saved method no longer charges: its receipt
   * of her Stop, or the household's push that nothing is saved, while the invoice's own push has
   * not caught up. Present only as true, and only while the copy still holds the method.
   */
  readonly methodStopped?: true;
}

/** An invoice's own page, from GET /api/invoices/:id. */
export interface InvoicePlace {
  /** The business's day the answer was made on. */
  readonly today: string;
  readonly household: HouseholdShown;
  readonly invoice: InvoicePage;
  /**
   * Each home she may see, by its label, which names the invoice's home among several. An answer
   * without them is read as none.
   */
  readonly homes: readonly HomeName[];
}

/**
 * Asks GET /api/invoices/:id for one invoice. The id is encoded as the path's last part, so
 * whatever it holds names one invoice and never another path. A 404 is an invoice the Worker does
 * not find for her, which is also how an invoice of another household, and every invoice to a
 * person shown no money, reads; with `arriving`, the Worker says it may still be on its way, her
 * first fill still arriving (ops's plan, section 7, ask 5).
 */
export function getInvoice(id: string): Promise<PlaceAnswer<InvoicePlace>> {
  return getPlace(`/api/invoices/${encodeURIComponent(id)}`, readInvoicePlace, {
    arriving: true,
  });
}

function readFeedbackAsk(value: unknown): FeedbackAsk | undefined {
  if (!isFields(value) || typeof value.id !== 'string') return undefined;
  const { recorded } = value;
  return only({
    id: value.id,
    recorded: typeof recorded === 'boolean' ? recorded : undefined,
    face: textOf(value.face),
    afterPush: value.afterPush === true ? (true as const) : undefined,
    notAsked: value.notAsked === true ? (true as const) : undefined,
  });
}

/** Text with something in it besides spaces, without the spaces around it, else nothing. */
function filledOf(value: unknown): string | undefined {
  const text = textOf(value)?.trim();
  return text === undefined || text === '' ? undefined : text;
}

/** An https address, else nothing, so a link drawn from it only ever opens a web page. */
function httpsOf(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try {
    return new URL(value).protocol === 'https:' ? value : undefined;
  } catch {
    return undefined;
  }
}

function readZelle(value: unknown): Zelle | undefined {
  if (!isFields(value)) return undefined;
  const tag = filledOf(value.tag);
  if (tag === undefined) return undefined;
  return only({
    tag,
    qrUrl: httpsOf(value.qrUrl),
    name: filledOf(value.name),
    memo: filledOf(value.memo),
  });
}

/** The business's hours, only whole: a start, an end, and weekdays each a whole number. */
function readHours(value: unknown): BusinessHours | undefined {
  if (!isFields(value)) return undefined;
  const { start, end, days } = value;
  if (typeof start !== 'string' || typeof end !== 'string' || !Array.isArray(days)) {
    return undefined;
  }
  return days.every((day) => Number.isInteger(day)) ? { start, end, days } : undefined;
}

function readPageAutopay(
  value: unknown,
  read: InvoiceAutopay | undefined,
): PageAutopay | undefined {
  if (read === undefined || !isFields(value)) return undefined;
  return only({
    ...read,
    hours: readHours(value.hours),
    receiptByEmail: value.receiptByEmail === true,
  });
}

const ZELLE_ASK_STATES = ['sending', 'failed'] as const;

function readZelleAsk(value: unknown): ZelleAsk | undefined {
  if (!isFields(value) || typeof value.id !== 'string') return undefined;
  const { id, state } = value;
  if (state === 'sent') return only({ id, state: 'sent' as const, stage: textOf(value.stage) });
  if (state === 'refused') return only({ id, state: 'refused' as const, code: textOf(value.code) });
  const known = ZELLE_ASK_STATES.find((each) => each === state);
  return known === undefined ? undefined : { id, state: known };
}

function readPayAsk(value: unknown): PayAsk | undefined {
  if (!isFields(value) || value.state !== 'refused') return undefined;
  return only({ state: 'refused' as const, code: textOf(value.code) });
}

function readInvoicePage(value: unknown): InvoicePage | null {
  const invoice = readInvoice(value);
  if (invoice === null || !isFields(value)) return null;
  const charged = isFields(value.charged) ? value.charged : null;
  return only({
    ...invoice,
    autopay: readPageAutopay(value.autopay, invoice.autopay),
    zelleAsk: readZelleAsk(value.zelleAsk),
    payAsk: readPayAsk(value.payAsk),
    charged:
      invoice.charged === undefined
        ? undefined
        : only({ ...invoice.charged, amountCents: numberOf(charged?.amountCents) }),
    feedbackAsk: readFeedbackAsk(value.feedbackAsk),
    methodStopped: value.methodStopped === true ? (true as const) : undefined,
  });
}

function readInvoicePlace(body: unknown): InvoicePlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household } = body;
  if (!isFields(household) || typeof household.id !== 'string') return null;
  const invoice = readInvoicePage(body.invoice);
  if (invoice === null) return null;
  return {
    today: body.today,
    household: readHousehold(household.id, household),
    invoice,
    homes: listOf(body.homes, readHomeName),
  };
}

function readPayments(body: unknown): PaymentsPlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household, arriving } = body;
  if (!isFields(household) || typeof household.id !== 'string' || !isFields(arriving)) return null;
  return only({
    today: body.today,
    household: readHousehold(household.id, household),
    arriving: { invoices: arriving.invoices === true },
    autopay: readSavedMethod(body.autopay),
    stopAsk: readStopAsk(body.stopAsk),
    saveOffered: body.saveOffered === true ? (true as const) : undefined,
    homes: listOf(body.homes, readHomeName),
    toPay: listOf(body.toPay, readInvoice),
    paid: listOf(body.paid, readInvoice),
  });
}

/*
 * ACCOUNT (slice A1): what the Worker's `routes/account.ts` answers, the page about her.
 */

/**
 * Who she is, as the office has her: her role, her name, the address she signed in with (or, signed
 * in by number, hers cut to its first character and its domain), the number she signed in with, or
 * her number on file by its last four, and for a member ops limited to some homes their labels.
 * Never anybody else's.
 */
export interface You {
  /** `client` or `member`, the seam's words. */
  readonly role: 'client' | 'member';
  readonly name?: string;
  readonly email?: string;
  /** The number she signed in with, in E.164, when she signed in by one. */
  readonly mobile?: string;
  /** The last four digits of her number on file. */
  readonly mobileEnding?: string;
  /** A member's homes by their labels, when ops limited her to some; absent when she sees all. */
  readonly homes?: readonly string[];
  /**
   * What she may ask about her own details now (round six, slice S2): `person.contact_change`,
   * where the door would take it and none of hers waits, as People and sign-in offers it.
   */
  readonly offers: readonly string[];
  /** Her own ask to change her name, email or mobile while it waits: `sent` or `received`. */
  readonly contactAsk?: PeopleAsk;
}

/** The household's people as Account names them: three first names at most, and how many more. */
export interface PeopleNamed {
  readonly names: readonly string[];
  readonly more: number;
  /** She is the only person on the household. */
  readonly onlyYou?: true;
}

/**
 * A home's regular cleans: its label, ops's cadence and weekday codes, and a pause while it has not
 * ended, its days `YYYY-MM-DD`; and `paused` while ops holds the standing schedule paused, with the
 * days or, where its agreement is paused with none left to say, alone (the Worker's `standingOf`,
 * P1). Never the window, which no reader here reads, and never anything from the household's own
 * `status`, which decides nothing about the regular cleans (ops's 11.6, question 1).
 */
export interface RegularCleans {
  readonly id: string;
  readonly label?: string;
  readonly cadence?: string;
  readonly weekday?: string;
  readonly pausedFrom?: string;
  readonly pausedUntil?: string;
  readonly paused?: true;
}

/** Account, from GET /api/account. */
export interface AccountPlace {
  readonly today: string;
  readonly household: HouseholdShown;
  /** Which sections have not come down from ops yet. */
  readonly arriving: { readonly household: boolean; readonly homes: boolean };
  readonly you: You;
  /** A client's alone: the saved method while one stands, null when nothing is. */
  readonly autopay?: SavedMethod | null;
  /** A client's alone: her newest Stop of the saved method that stands. */
  readonly stopAsk?: StopAsk;
  /**
   * A client's alone (slice A4): true while an owed invoice is being charged now by a saved method,
   * which a Stop does not cancel; absent otherwise.
   */
  readonly chargingNow?: true;
  /** A client's alone, once the household has come down. */
  readonly people?: PeopleNamed;
  /** How many phones and computers her sign-in is live on, ten at most; none read is none. */
  readonly sessions: number;
  readonly homes: readonly RegularCleans[];
  /**
   * A client's alone: each home's regular cleans with what she may ask about them and where the
   * household's asks stand (round five, slice C3). Absent for anyone else.
   */
  readonly cleans?: readonly HomeCleans[];
  /**
   * True once ops has pushed her choices of what we send her (ops's section 11.3, P114), for a
   * client and a member alike: Account offers "What we send you". Absent until then.
   */
  readonly sending?: true;
}

/** Asks GET /api/account for Account. */
export function getAccount(): Promise<PlaceAnswer<AccountPlace>> {
  return getPlace('/api/account', readAccount);
}

function readYou(value: unknown): You | null {
  if (!isFields(value)) return null;
  const role = value.role === 'client' || value.role === 'member' ? value.role : null;
  if (role === null) return null;
  return only<You>({
    role,
    name: textOf(value.name),
    email: textOf(value.email),
    mobile: textOf(value.mobile),
    mobileEnding: textOf(value.mobileEnding),
    homes: Array.isArray(value.homes) ? wordsOf(value.homes) : undefined,
    offers: wordsOf(value.offers),
    contactAsk: readPeopleAsk(value.contactAsk),
  });
}

function readPeople(value: unknown): PeopleNamed | undefined {
  if (!isFields(value)) return undefined;
  const more = numberOf(value.more);
  return only({
    names: wordsOf(value.names),
    more: more !== undefined && Number.isSafeInteger(more) && more > 0 ? more : 0,
    onlyYou: value.onlyYou === true ? (true as const) : undefined,
  });
}

function readRegularCleans(value: unknown): RegularCleans | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  return only({
    id: value.id,
    label: textOf(value.label),
    cadence: textOf(value.cadence),
    weekday: textOf(value.weekday),
    pausedFrom: textOf(value.pausedFrom),
    pausedUntil: textOf(value.pausedUntil),
    paused: value.paused === true ? (true as const) : undefined,
  });
}

function readAccount(body: unknown): AccountPlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household, arriving } = body;
  if (!isFields(household) || typeof household.id !== 'string' || !isFields(arriving)) return null;
  const you = readYou(body.you);
  if (you === null) return null;
  const sessions = countOf(body.sessions);
  return only({
    today: body.today,
    household: readHousehold(household.id, household),
    arriving: { household: arriving.household === true, homes: arriving.homes === true },
    you,
    autopay: 'autopay' in body ? readSavedMethod(body.autopay) : undefined,
    stopAsk: readStopAsk(body.stopAsk),
    chargingNow: body.chargingNow === true ? (true as const) : undefined,
    people: readPeople(body.people),
    sessions: sessions ?? 0,
    homes: listOf(body.homes, readRegularCleans),
    cleans: 'cleans' in body ? listOf(body.cleans, readHomeCleans) : undefined,
    sending: body.sending === true ? (true as const) : undefined,
  });
}

/*
 * HER ASKS ABOUT HER REGULAR CLEANS (round five, slice C3, ops's section 12.4): what Account and
 * Your agreement are given of each home, for a client alone (the Worker's `cleansAsksView`): what
 * she may ask the office now, what an ask that waits holds back, the services an ending may name,
 * and the newest ask of each type with where it stands. Read into the fields the screens draw: of
 * what was asked, the days, the cadence, the weekday, the service and her own window, never a note,
 * which no screen draws, and nothing of another person's own words, which the Worker never sends.
 */

/** The four asks about her regular cleans, in the order a screen offers them. */
export const CLEANS_ASK_TYPES = [
  'schedule.pause',
  'schedule.resume',
  'schedule.change',
  'service.end',
] as const;

export type CleansAskType = (typeof CLEANS_ASK_TYPES)[number];

const isCleansAsk = (value: unknown): value is CleansAskType =>
  typeof value === 'string' && (CLEANS_ASK_TYPES as readonly string[]).includes(value);

/** The ones of the four in a list the Worker gave, each once, in the order given. */
const cleansAsksOf = (value: unknown): CleansAskType[] => [
  ...new Set(wordsOf(value).filter(isCleansAsk)),
];

/** One of her household's asks about a home's regular cleans, as a screen says it. */
export interface CleansAskShown {
  readonly id: string;
  readonly type: CleansAskType;
  /** Whether she asked for it herself. */
  readonly mine: boolean;
  /** Who asked, by the name the household lists, when it was not her. */
  readonly askedBy?: string;
  /** `sent`, `received`, `accepted`, `declined`, `cancelled`, `refused` or `stopped`. */
  readonly state: string;
  /** Why ops cancelled it, such as `schedule_ended`. */
  readonly reason?: string;
  /** Ops's refusal code, such as `already_paused`. */
  readonly code?: string;
  /** The office's own words, written or approved by a person there. */
  readonly answer?: string;
  /** What was asked: the days `YYYY-MM-DD`, and ops's codes for the rest. */
  readonly from?: string;
  /** A pause's last day; absent for no return date yet. */
  readonly until?: string;
  readonly cadence?: string;
  readonly weekday?: string;
  /** Her own window, in her words: the Worker sends it to her alone. */
  readonly window?: string;
  readonly service?: string;
  readonly lastDay?: string;
  /**
   * Present, and true, on an ending she asked as a cancel: what Account's button said when she
   * asked, as the Worker read it then and kept on the ask (its `cancel`). Never read from the home's
   * agreements as they stand now, which ops ends as she asked (the review of the fifth
   * client-journey walk's leftovers, 2026-10-20).
   */
  readonly cancel?: true;
}

/** A home's regular cleans with what she may ask about them, and where the asks stand. */
export interface HomeCleans extends RegularCleans {
  /** What she may ask the office about the home now. */
  readonly offers: readonly CleansAskType[];
  /** What an ask of its type that waits on the office holds back. */
  readonly held: readonly CleansAskType[];
  /** The services an ending may name, ops's codes, in ops's order. */
  readonly services: readonly string[];
  /**
   * Of `services`, those none of whose live agreements at the home comes again, every one one-time:
   * an ending of one cancels it (the Worker's `oneTimeServicesAt`). Absent where there is none.
   */
  readonly oneTime?: readonly string[];
  /**
   * Where her ending is offered as a cancel, the one open one-time visit after today at the home of
   * each service of `oneTime` that has exactly one, which the cancel names by its kind and its day,
   * and whose day before its sheet sends as `last_day` (the Worker's `cancelVisits`, ops's answer on
   * ops #76, 2026-10-22, and the review of 2621dcd). Absent where none has one.
   */
  readonly cancelVisits?: readonly CancelVisit[];
  /** The newest ask of each type about the home. */
  readonly requests: readonly CleansAskShown[];
}

/** A visit a cancel names: the service it is of, its day, `YYYY-MM-DD`, and ops's kind. */
export interface CancelVisit {
  readonly service: string;
  readonly date: string;
  readonly kind: string;
}

function readCancelVisit(value: unknown): CancelVisit | null {
  if (!isFields(value)) return null;
  const { service, date, kind } = value;
  return typeof service === 'string' && typeof date === 'string' && typeof kind === 'string'
    ? { service, date, kind }
    : null;
}

function readCleansAsk(value: unknown): CleansAskShown | null {
  if (!isFields(value)) return null;
  const { id, type, mine, state } = value;
  if (typeof id !== 'string' || !isCleansAsk(type) || typeof state !== 'string') return null;
  if (typeof mine !== 'boolean') return null;
  const asked = isFields(value.asked) ? value.asked : {};
  return only<CleansAskShown>({
    id,
    type,
    mine,
    askedBy: textOf(value.askedBy),
    state,
    reason: textOf(value.reason),
    code: textOf(value.code),
    answer: textOf(value.answer),
    from: textOf(asked.from),
    until: textOf(asked.until),
    cadence: textOf(asked.cadence),
    weekday: textOf(asked.weekday),
    window: textOf(asked.window),
    service: textOf(asked.service),
    lastDay: textOf(asked.lastDay),
    cancel: value.cancel === true ? (true as const) : undefined,
  });
}

function readHomeCleans(value: unknown): HomeCleans | null {
  const home = readRegularCleans(value);
  if (home === null || !isFields(value)) return null;
  const services = wordsOf(value.services);
  // Only among the services an ending may name, so a cancel never names another.
  const oneTime = wordsOf(value.oneTime).filter((service) => services.includes(service));
  // Only of a service it cancels, so a cancel never names a visit of another.
  const cancelVisits = listOf(value.cancelVisits, readCancelVisit).filter((visit) =>
    oneTime.includes(visit.service),
  );
  return {
    ...home,
    offers: cleansAsksOf(value.offers),
    held: cleansAsksOf(value.held),
    services,
    ...(oneTime.length > 0 ? { oneTime } : {}),
    ...(cancelVisits.length > 0 ? { cancelVisits } : {}),
    requests: listOf(value.requests, readCleansAsk),
  };
}

/*
 * HER AGREEMENTS (round five, slice C2): what `GET /api/account/agreements` answers (the Worker's
 * `agreementsPageView`, ops's section 12.1), read into the fields Your agreement draws. The Worker
 * gives a member no money and a client her own; the screen draws money only where the household
 * says she pays here too. Each figure is read only as whole cents, and the pricing page only as a
 * plain https address, so no other kind of link is ever drawn.
 */

/** A standing add-on of an agreement: its words, how many, and to a client its price each. */
export interface AgreementAddon {
  readonly label: string;
  readonly quantity?: number;
  readonly unitPriceCents?: number;
}

/** The written scope an agreement pins, as she was shown it. */
export interface AgreementScope {
  readonly version?: number;
  /** The day that version holds from, `YYYY-MM-DD`. */
  readonly effectiveFrom?: string;
  readonly body?: string;
}

/** A home watch season: how many visits it expects, and to a client what it was quoted at. */
export interface AgreementSeason {
  readonly visits?: number;
  readonly totalCents?: number;
}

/**
 * One agreement, in ops's codes and words: its home, service, cadence, weekday and state, its days
 * `YYYY-MM-DD`, a pause that has not ended, the card's line, the scope, a season and the standing
 * add-ons; and for a client the price of one visit, how it was set, whether the line is a starting
 * price, the day the deep clean was said, when invoices are due and the pricing page. Never a
 * window, a time or a length, which no reader here reads.
 */
export interface Agreement {
  readonly id: string;
  readonly homeId?: string;
  readonly homeLabel?: string;
  readonly service?: string;
  readonly cadence?: string;
  readonly weekday?: string;
  readonly state?: string;
  readonly startsOn?: string;
  readonly endsOn?: string;
  readonly agreedOn?: string;
  readonly pausedFrom?: string;
  readonly pausedUntil?: string;
  readonly line?: string;
  readonly scope?: AgreementScope;
  readonly season?: AgreementSeason;
  readonly addons: readonly AgreementAddon[];
  readonly priceCents?: number;
  readonly priced?: 'published' | 'walkthrough';
  readonly publishedFrom?: true;
  readonly deepCleanSaidOn?: string;
  readonly invoiceTermsDays?: number;
  readonly pricingUrl?: string;
}

/**
 * The household's saved method as Your agreement says it, a client's alone: Stripe's words for it
 * and its type, the business's day it was agreed, the words ops holds for the version it was agreed
 * under, as ops sends them, and where it was agreed. Never who agreed.
 */
export interface AutopayTerms {
  readonly label?: string;
  readonly methodType?: string;
  readonly agreedOn?: string;
  readonly words?: string;
  readonly agreedThrough?: string;
}

/** Your agreement, from GET /api/account/agreements. */
export interface AgreementsPlace {
  readonly today: string;
  readonly household: HouseholdShown;
  /** Which sections have not come down from ops yet. */
  readonly arriving: { readonly household: boolean; readonly agreements: boolean };
  /** The day of the household's first agreement, once ops has sent it. */
  readonly bookedOn?: string;
  /** By home, the live first and the newest first, as the Worker orders them. */
  readonly agreements: readonly Agreement[];
  /** A client's alone: the saved method while one stands, null when nothing is. */
  readonly autopay?: AutopayTerms | null;
  /**
   * A client's alone: her newest Stop of the saved method that stands, as Account reads it (round
   * six, slice S2).
   */
  readonly stopAsk?: StopAsk;
  /**
   * A client's alone: each home's regular cleans with what she may ask about them and where the
   * household's asks stand (round five, slice C3), as Account reads them. Absent for anyone else.
   */
  readonly cleans?: readonly HomeCleans[];
}

/** Asks GET /api/account/agreements for Your agreement. */
export function getAgreements(): Promise<PlaceAnswer<AgreementsPlace>> {
  return getPlace('/api/account/agreements', readAgreements);
}

/** Money in whole cents, nothing below none. */
const centsOf = (value: unknown): number | undefined => {
  const cents = numberOf(value);
  return cents !== undefined && Number.isSafeInteger(cents) && cents >= 0 ? cents : undefined;
};

/** A count or a number of days, a whole number above nothing. */
const wholeOf = (value: unknown): number | undefined => countOf(value);

/** An object of the fields that are there, or nothing for one with none. */
function filledOrNothing<T extends object>(fields: T): T | undefined {
  const kept = only(fields);
  return Object.keys(kept).length === 0 ? undefined : kept;
}

function readAgreementScope(value: unknown): AgreementScope | undefined {
  if (!isFields(value)) return undefined;
  return filledOrNothing<AgreementScope>({
    version: wholeOf(value.version),
    effectiveFrom: textOf(value.effectiveFrom),
    body: textOf(value.body),
  });
}

function readSeason(value: unknown): AgreementSeason | undefined {
  if (!isFields(value)) return undefined;
  return filledOrNothing<AgreementSeason>({
    visits: wholeOf(value.visits),
    totalCents: centsOf(value.totalCents),
  });
}

function readAgreementAddon(value: unknown): AgreementAddon | null {
  if (!isFields(value) || typeof value.label !== 'string') return null;
  return only<AgreementAddon>({
    label: value.label,
    quantity: wholeOf(value.quantity),
    unitPriceCents: centsOf(value.unitPriceCents),
  });
}

function readAgreement(value: unknown): Agreement | null {
  if (!isFields(value) || typeof value.id !== 'string') return null;
  return only<Agreement>({
    id: value.id,
    homeId: textOf(value.homeId),
    homeLabel: textOf(value.homeLabel),
    service: textOf(value.service),
    cadence: textOf(value.cadence),
    weekday: textOf(value.weekday),
    state: textOf(value.state),
    startsOn: textOf(value.startsOn),
    endsOn: textOf(value.endsOn),
    agreedOn: textOf(value.agreedOn),
    pausedFrom: textOf(value.pausedFrom),
    pausedUntil: textOf(value.pausedUntil),
    line: textOf(value.line),
    scope: readAgreementScope(value.scope),
    season: readSeason(value.season),
    addons: listOf(value.addons, readAgreementAddon),
    priceCents: centsOf(value.priceCents),
    priced:
      value.priced === 'published' || value.priced === 'walkthrough' ? value.priced : undefined,
    publishedFrom: value.publishedFrom === true ? (true as const) : undefined,
    deepCleanSaidOn: textOf(value.deepCleanSaidOn),
    invoiceTermsDays: wholeOf(value.invoiceTermsDays),
    pricingUrl: httpsOf(value.pricingUrl),
  });
}

function readAutopayTerms(value: unknown): AutopayTerms | null {
  if (!isFields(value)) return null;
  return only<AutopayTerms>({
    label: textOf(value.label),
    methodType: textOf(value.methodType),
    agreedOn: textOf(value.agreedOn),
    words: textOf(value.words),
    agreedThrough: textOf(value.agreedThrough),
  });
}

function readAgreements(body: unknown): AgreementsPlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household, arriving } = body;
  if (!isFields(household) || typeof household.id !== 'string' || !isFields(arriving)) return null;
  return only<AgreementsPlace>({
    today: body.today,
    household: readHousehold(household.id, household),
    arriving: {
      household: arriving.household === true,
      agreements: arriving.agreements === true,
    },
    bookedOn: textOf(body.bookedOn),
    agreements: listOf(body.agreements, readAgreement),
    autopay: 'autopay' in body ? readAutopayTerms(body.autopay) : undefined,
    stopAsk: readStopAsk(body.stopAsk),
    cleans: 'cleans' in body ? listOf(body.cleans, readHomeCleans) : undefined,
  });
}

/*
 * PEOPLE AND SIGN-IN (slice A3): what the Worker's `GET /api/account/people` answers, Account's page
 * about the household's people and where she is signed in (`peoplePageView`, `api/src/seam/
 * views.ts`). Never an address, a number or anybody's id: a person is named for an ask by home's own
 * reference, which only the request door turns back into ops's id.
 */

/** Whether a person can sign in here: a door ops granted, one it invited her to, or none. */
export type SignInState = 'granted' | 'invited' | 'none';

const SIGN_IN_STATES: readonly SignInState[] = ['granted', 'invited', 'none'];

/**
 * Where one of her own people asks stands: `sent`, `received`, `declined`, `cancelled` with ops's
 * reason, `refused` with ops's code, or `stopped`; and the office's own words once it has decided.
 * One ops accepted is not here, since what she asked has then happened.
 */
export interface PeopleAsk {
  readonly state: string;
  readonly reason?: string;
  readonly code?: string;
  readonly answer?: string;
}

/** Another person on the household, as a client is shown her. */
export interface PersonRow {
  /** Home's reference for her, which an ask about her names: never ops's id. */
  readonly ref: string;
  /** The name the office typed. */
  readonly name?: string;
  /** Ops's word for her on the household, such as `primary` or `property_manager`. */
  readonly householdRole?: string;
  /** What her door lets her do here, the seam's `client` or `member`; absent with no door. */
  readonly door?: 'client' | 'member';
  /** The labels of the homes her door is limited to; absent for every home. */
  readonly homes?: readonly string[];
  /** The same homes by ops's site id, which an ask to change them names. */
  readonly homeIds?: readonly string[];
  readonly signIn: SignInState;
  /** The business's day ops invited her, while she is invited and ops said when. */
  readonly invitedOn?: string;
  /** What she may ask about this person: `person.change`, `person.remove`. */
  readonly offers: readonly string[];
  /**
   * What the door would take about this person but for an ask of the same type about somebody else
   * that waits on the office (round six, slice S2); absent for none.
   */
  readonly held?: readonly string[];
  /**
   * Her own newest ask of each type about this person, while it is said, each with its type (round
   * six, slice S2); absent for none.
   */
  readonly asks?: readonly (PeopleAsk & { readonly type: string })[];
}

/** Her own row: her role, her name, ops's word for her, and her own two asks. */
export interface PeopleYou {
  readonly role: 'client' | 'member';
  readonly name?: string;
  readonly householdRole?: string;
  /** `person.contact_change` and `person.leave`, where the door would take each now. */
  readonly offers: readonly string[];
  /** She is the household's only client at home, so her leave would be refused (`last_client`). */
  readonly lastClient?: true;
  readonly contactAsk?: PeopleAsk;
  readonly leaveAsk?: PeopleAsk;
}

/** Her own newest ask to add someone, with the name and the role she gave. */
export interface AddAsk extends PeopleAsk {
  readonly name?: string;
  readonly role?: 'client' | 'member';
}

/** A session she is signed in on: its device as codes from home's two lists, and its days. */
export interface SessionShown {
  readonly browser?: string;
  readonly system?: string;
  /** The business's day it began, `YYYY-MM-DD`. */
  readonly signedIn: string;
  /** The business's day it was last used. */
  readonly lastUsed: string;
}

/** Where she is signed in: this one, her other live sessions, and how many more. */
export interface SignedInShown {
  /** This browser's session, or null where the Worker gave none this code can read. */
  readonly here: SessionShown | null;
  readonly others: readonly SessionShown[];
  /** How many more of her other live sessions there are than those listed. */
  readonly more: number;
  /** The Worker's count stopped at its limit, so there may be more still. */
  readonly moreAtLeast?: true;
}

/** People and sign-in, from GET /api/account/people. */
export interface PeoplePlace {
  readonly today: string;
  readonly household: HouseholdShown;
  readonly arriving: { readonly household: boolean };
  readonly you: PeopleYou;
  /** A client's alone, once the household has come down: everybody on it but her. */
  readonly people?: readonly PersonRow[];
  /** What she may ask about the household's people as a whole: `person.add`. */
  readonly offers: readonly string[];
  /** What an ask already waiting holds, whoever made it. */
  readonly held: readonly string[];
  /** A client's alone: the homes she may name for a person to add or change. */
  readonly homes: readonly HomeName[];
  /** A client's alone: her own newest ask to add someone, while it is said. */
  readonly addAsk?: AddAsk;
  readonly signedIn: SignedInShown;
}

/** Asks GET /api/account/people for People and sign-in. */
export function getAccountPeople(): Promise<PlaceAnswer<PeoplePlace>> {
  return getPlace('/api/account/people', readPeoplePlace);
}

/** Home's reference for a person: 32 lower-case hex characters, as the Worker writes it. */
const PERSON_REF = /^[0-9a-f]{32}$/;

/** One of the seam's two roles a door carries, or nothing. */
const doorOf = (value: unknown): 'client' | 'member' | undefined =>
  value === 'client' || value === 'member' ? value : undefined;

function readPeopleAsk(value: unknown): PeopleAsk | undefined {
  if (!isFields(value) || typeof value.state !== 'string') return undefined;
  return only({
    state: value.state,
    reason: textOf(value.reason),
    code: textOf(value.code),
    answer: textOf(value.answer),
  });
}

function readPersonRow(value: unknown): PersonRow | null {
  if (!isFields(value) || typeof value.ref !== 'string' || !PERSON_REF.test(value.ref)) return null;
  const signIn = SIGN_IN_STATES.find((state) => state === value.signIn);
  if (signIn === undefined) return null;
  const asks = listOf(value.asks, readAskAbout);
  const held = Array.isArray(value.held) ? wordsOf(value.held) : [];
  return only<PersonRow>({
    ref: value.ref,
    name: textOf(value.name),
    householdRole: textOf(value.householdRole),
    door: doorOf(value.door),
    homes: Array.isArray(value.homes) ? wordsOf(value.homes) : undefined,
    homeIds: Array.isArray(value.homeIds) ? wordsOf(value.homeIds) : undefined,
    signIn,
    invitedOn: textOf(value.invitedOn),
    offers: wordsOf(value.offers),
    held: held.length > 0 ? held : undefined,
    asks: asks.length > 0 ? asks : undefined,
  });
}

/** One of her asks about a person, with its type, or null for one with no type or no state. */
function readAskAbout(value: unknown): (PeopleAsk & { readonly type: string }) | null {
  const asked = readPeopleAsk(value);
  const type = isFields(value) ? textOf(value.type) : undefined;
  return asked === undefined || type === undefined ? null : { type, ...asked };
}

function readPeopleYou(value: unknown): PeopleYou | null {
  if (!isFields(value)) return null;
  const role = doorOf(value.role);
  if (role === undefined) return null;
  return only<PeopleYou>({
    role,
    name: textOf(value.name),
    householdRole: textOf(value.householdRole),
    offers: wordsOf(value.offers),
    lastClient: value.lastClient === true ? (true as const) : undefined,
    contactAsk: readPeopleAsk(value.contactAsk),
    leaveAsk: readPeopleAsk(value.leaveAsk),
  });
}

function readAddAsk(value: unknown): AddAsk | undefined {
  const asked = readPeopleAsk(value);
  if (asked === undefined || !isFields(value)) return undefined;
  return only<AddAsk>({ ...asked, name: textOf(value.name), role: doorOf(value.role) });
}

function readSessionShown(value: unknown): SessionShown | null {
  if (!isFields(value)) return null;
  const { signedIn, lastUsed } = value;
  if (typeof signedIn !== 'string' || typeof lastUsed !== 'string') return null;
  return only<SessionShown>({
    browser: textOf(value.browser),
    system: textOf(value.system),
    signedIn,
    lastUsed,
  });
}

function readSignedIn(value: unknown): SignedInShown {
  const fields = isFields(value) ? value : {};
  return only<SignedInShown>({
    here: readSessionShown(fields.here),
    others: listOf(fields.others, readSessionShown),
    more: countOf(fields.more) ?? 0,
    moreAtLeast: fields.moreAtLeast === true ? (true as const) : undefined,
  });
}

function readPeoplePlace(body: unknown): PeoplePlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household, arriving } = body;
  if (!isFields(household) || typeof household.id !== 'string' || !isFields(arriving)) return null;
  const you = readPeopleYou(body.you);
  if (you === null) return null;
  return only<PeoplePlace>({
    today: body.today,
    household: readHousehold(household.id, household),
    arriving: { household: arriving.household === true },
    you,
    people: Array.isArray(body.people) ? listOf(body.people, readPersonRow) : undefined,
    offers: wordsOf(body.offers),
    held: wordsOf(body.held),
    homes: listOf(body.homes, readHomeName),
    addAsk: readAddAsk(body.addAsk),
    signedIn: readSignedIn(body.signedIn),
  });
}

/** What signing out everywhere else comes to. */
export type OthersSignedOut =
  /** The Worker ended every other session of hers, or found none to end. */
  | 'done'
  /** 401: this session has ended too, and the door is the way back in. */
  | 'signed_out'
  /** 403 `step_up_required`: the Worker asks for a fresh code first, and ended nothing. */
  | 'step_up'
  /** Anything else, a request that never reached the Worker included: nothing was ended. */
  | 'failed';

/**
 * Signs out every other phone and computer she is signed in on, POST /api/auth/signout-others,
 * behind a fresh code and sign-out's Origin check (slice P2 of the pre-launch fixes): the Worker
 * ends every other live session of the identifier this one proved, and answers 204, or answers 403
 * `step_up_required` where no code was typed on this session in the last fifteen minutes. Its answer
 * is read to its end, within `REQUEST_LIMIT_MS`.
 */
export async function signOutOthers(): Promise<OthersSignedOut> {
  try {
    return await within('/api/auth/signout-others', { method: 'POST' }, async (response) => {
      if (response.status === 403) {
        const body = (await response.json().catch(() => null)) as Fields | null;
        return isFields(body) && body.error === 'step_up_required' ? 'step_up' : 'failed';
      }
      await letGo(response);
      if (response.ok) return 'done';
      return response.status === 401 ? 'signed_out' : 'failed';
    });
  } catch {
    return 'failed';
  }
}

/*
 * ASKING THE OFFICE: POST /api/requests, the request door (the Worker's `routes/requests.ts`). The
 * form mints `key`, a uuid, once for what it asks, so a double tap or a retry names one request and
 * the door writes one row. What the door answers is read into what the screen does next, and never
 * into a decision: sent means the office will have it, and nothing more (CLAUDE.md binding rule 1).
 */

/**
 * The door an ask goes to: the plain one behind a session, or one of the two behind a fresh code
 * typed in the last fifteen minutes, `person` for a person to add and `billing` for changing how
 * the household pays (CLAUDE.md binding rule 5).
 */
export type Door = 'session' | 'person' | 'billing';

/** What a form asks: its key, the seam's type, the household shown, and the seam's payload. */
export interface Ask {
  readonly key: string;
  readonly type: string;
  readonly household: string;
  readonly payload: Readonly<Record<string, unknown>>;
}

export type AskAnswer =
  /** 202: written, and on its way to the office, as the request `id`. */
  | { readonly outcome: 'sent'; readonly id: string }
  /** 401: the session has ended, and the door is the way back in. */
  | { readonly outcome: 'signed_out' }
  /**
   * 403 `step_up_required`: this door asks for a fresh code first, and nothing was written. Once
   * she has typed one, the same ask with the same key goes through.
   */
  | { readonly outcome: 'step_up' }
  /** 404: the household or the visit is not hers to ask about now; the place is read again. */
  | { readonly outcome: 'not_found' }
  /** 409 `not_open`: the visit, as ops last pushed it, can no longer be changed. */
  | { readonly outcome: 'not_open' }
  /**
   * 409 `already_asked`: the same ask, somebody's on the household, already waits on the office,
   * and nothing was written. Asking again is refused again, so the place is read again, and says it.
   * `requestId` is the first's, which the door names as ops does (the seam, section 4 at 0b51e49,
   * "The caps, held in the write"), and is absent when the door named none.
   */
  | { readonly outcome: 'already_asked'; readonly requestId?: string }
  /** 422 `looks_like_a_code`: a field holds what looks like a code, and nothing was stored. */
  | { readonly outcome: 'looks_like_a_code'; readonly field: string }
  /**
   * 429 `too_many`: she or the household is past one of the door's own limits, an hour's or a day's
   * asks or the asks waiting on a retry, and nothing was written or sent. A later try may go:
   * `limit` says whose count it is and from when, where the Worker says it (round six, slice S1).
   */
  | { readonly outcome: 'too_many'; readonly limit?: TooMany }
  /**
   * Any other refusal: it will not go through as it is. `code` is the door's word for it, such as
   * ops's own `payments_off` or `invoice_not_open` for a refusal about money, which a screen that
   * pays acts on, and is absent when the door gave none.
   */
  | { readonly outcome: 'refused'; readonly code?: string }
  /**
   * Anything else: it did not go through, and may be tried again. `offline` where it never reached
   * the Worker because the connection dropped (`within`), so a screen can say so; absent for a
   * failure the Worker gave, and for an ask with no answer in its time (`REQUEST_LIMIT_MS`).
   */
  | { readonly outcome: 'failed'; readonly offline?: true };

/**
 * Whose count of the request door's own limits a press was past, and when a try may go again (round
 * six, slice S1): `you` where the count is hers alone, `household` where it is the household's;
 * `after`, the business's time of day on a 24-hour clock, `HH:MM`, and whether it falls tomorrow, as
 * the Worker says it, so the tab never reads the phone's clock, or null where the count waits on the
 * office's side rather than on the clock.
 */
export interface TooMany {
  readonly whose: 'you' | 'household';
  readonly after: { readonly time: string; readonly tomorrow: boolean } | null;
}

/** A time of day as the Worker writes one, `HH:MM` on the 24-hour clock. */
const CLOCK_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Whose count and when, from the door's `429` body: `whose` `person` or `household`, and
 * `try_after`, `{ time, tomorrow }` or null. Undefined where it names nobody it knows, as from a
 * Worker before slice S1, which the screen says as it always did; a time that is not one is no time.
 */
function tooManyOf(body: Fields | null): TooMany | undefined {
  if (body === null) return undefined;
  const whose = body.whose === 'person' ? 'you' : body.whose === 'household' ? 'household' : null;
  if (whose === null) return undefined;
  const at = body.try_after;
  const after =
    isFields(at) &&
    typeof at.time === 'string' &&
    CLOCK_TIME.test(at.time) &&
    typeof at.tomorrow === 'boolean'
      ? { time: at.time, tomorrow: at.tomorrow }
      : null;
  return { whose, after };
}

/**
 * Sends what a form asks to POST /api/requests, or to the door behind a fresh code it names, and
 * reads the answer, within `REQUEST_LIMIT_MS`.
 */
export async function sendRequest(ask: Ask, door: Door = 'session'): Promise<AskAnswer> {
  try {
    return await within(
      door === 'session' ? '/api/requests' : `/api/requests/${door}`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(ask),
      },
      readAsked,
    );
  } catch (error) {
    return unansweredBy(error) === 'offline'
      ? { outcome: 'failed', offline: true }
      : { outcome: 'failed' };
  }
}

/** What the request door's answer says. */
async function readAsked(response: Response): Promise<AskAnswer> {
  const body = (await response.json().catch(() => null)) as Fields | null;
  const error = isFields(body) ? body.error : undefined;
  if (response.status === 202) {
    return isFields(body) && typeof body.id === 'string'
      ? { outcome: 'sent', id: body.id }
      : { outcome: 'failed' };
  }
  if (response.status === 401) return { outcome: 'signed_out' };
  if (response.status === 403 && error === 'step_up_required') return { outcome: 'step_up' };
  if (response.status === 404) return { outcome: 'not_found' };
  if (response.status === 409 && error === 'not_open') return { outcome: 'not_open' };
  if (response.status === 409 && error === 'already_asked') {
    return only({
      outcome: 'already_asked' as const,
      requestId: isFields(body) ? textOf(body.request_id) : undefined,
    });
  }
  if (response.status === 422 && error === 'looks_like_a_code' && isFields(body)) {
    return { outcome: 'looks_like_a_code', field: textOf(body.field) ?? '' };
  }
  if (response.status === 429) {
    return only({ outcome: 'too_many' as const, limit: tooManyOf(isFields(body) ? body : null) });
  }
  if (response.status >= 400 && response.status < 500) {
    return only({ outcome: 'refused' as const, code: textOf(error) });
  }
  return { outcome: 'failed' };
}

/** Her report that she sent an invoice by Zelle, as the request door takes it (section 11.7). */
export const ZELLE_SENT = 'invoice.zelle_sent';

/**
 * HER REPORT THAT SHE SENT AN INVOICE BY ZELLE (ops's section 11.7, round five's slice B2, P53),
 * sent through the request door like any ask, `invoice.zelle_sent` with the invoice and the day she
 * picked, `YYYY-MM-DD`, under the key the step minted for it, within `REQUEST_LIMIT_MS`. The door's
 * answer is read as any ask's (`AskAnswer`); the Worker then holds the invoice on its way, as home's
 * own bridge until ops answers, and carries what came of it on the invoice (`zelleReport`).
 */
export function reportZelleSent(
  key: string,
  household: string,
  invoiceId: string,
  sentOn: string,
): Promise<AskAnswer> {
  return sendRequest({
    key,
    type: ZELLE_SENT,
    household,
    payload: { invoice_id: invoiceId, sent_on: sentOn },
  });
}

/** Her word that she did not send it after all, as the request door takes it (11.7). */
export const ZELLE_WITHDRAWN = 'invoice.zelle_withdrawn';

/**
 * "I HAVE NOT SENT IT YET" (ops's section 11.7, "Taking it back", P53): her word that she did not
 * send the Zelle after all, taking her report back, sent through the request door like any ask,
 * `invoice.zelle_withdrawn` with the invoice alone, under the key the link minted for it, within
 * `REQUEST_LIMIT_MS`. The door's answer is read as any ask's (`AskAnswer`); the Worker then holds
 * nothing of home's own on the invoice, and carries what came of it (`zelleReport`).
 */
export function takeBackZelle(
  key: string,
  household: string,
  invoiceId: string,
): Promise<AskAnswer> {
  return sendRequest({ key, type: ZELLE_WITHDRAWN, household, payload: { invoice_id: invoiceId } });
}

/*
 * WHAT WE SEND YOU (ops's section 11.3, the draft of its phase 2; home's slices H5 and H7, P114):
 * her choices as ops last pushed them, read from GET /api/account/sending, and her switches, sent
 * through the request door like any ask. The Worker answers 404 until ops has pushed her choices,
 * and the page goes back to Account. Nothing here reads a choice as made: ops's push says it.
 */

/** The kinds of message she chooses between, in 11.3's order. */
export const SENDING_CATEGORIES = [
  'visit_changes',
  'visit_reminders',
  'visit_day',
  'home_checks',
  'invoices',
  'receipts',
] as const;
export type SendingCategory = (typeof SENDING_CATEGORIES)[number];

/** The times of day a reminder may go at (11.3). */
export const SEND_TIMES = ['morning', 'afternoon', 'evening'] as const;
export type SendTime = (typeof SEND_TIMES)[number];

/** How many days before a clean a reminder may go: on the day, or one to three days before. */
export const LEAD_DAYS = [0, 1, 2, 3] as const;

/**
 * Where her ask about one kind of message, or about her texts, stands while it is said: sending
 * before its first try, waiting while ops cannot be reached, sent once ops has it and its push has
 * not come, refused with ops's code, or stopped once home gave up.
 */
export interface SendingAsk {
  readonly state: 'sending' | 'waiting' | 'sent' | 'refused' | 'stopped';
  readonly choice?: 'on' | 'off';
  readonly leadDays?: number;
  readonly sendTime?: string;
  readonly code?: string;
}

/** One kind of message, for every home or for one, as ops last pushed it. */
export interface Setting {
  readonly category: SendingCategory;
  readonly homeId?: string;
  readonly email: 'on' | 'off' | 'not_offered';
  readonly byText?: true;
  readonly locked?: 'one_way' | 'email' | 'other';
  /** Whether the email switch is offered, held on by its lock, or not drawn as one at all. */
  readonly switch: 'offered' | 'locked' | 'none';
  readonly leadDays?: number;
  readonly sendTime?: string;
  readonly held?: { readonly from?: string; readonly until?: string };
  /** Who last changed it, how, and the business's day it was. */
  readonly changed?: { readonly on?: string; readonly by?: string; readonly how?: string };
  readonly officeCalls?: true;
  readonly ask?: SendingAsk;
}

/** Her choices for every home, or for one home by its label. */
export interface SendingSection {
  readonly homeId?: string;
  readonly label?: string;
  readonly settings: readonly Setting[];
}

/** "What we send you", from GET /api/account/sending. */
export interface SendingPlace {
  readonly today: string;
  readonly household: HouseholdShown;
  readonly role: 'client' | 'member';
  readonly email?: { readonly state: string };
  readonly texts?: { readonly state: string; readonly turnOnBy?: string };
  readonly paused?: { readonly from?: string; readonly until?: string; readonly home?: string };
  readonly textHours?: { readonly from: string; readonly until: string };
  readonly summary?: { readonly every: 'each' | 'daily'; readonly time?: string };
  readonly sections: readonly SendingSection[];
  readonly textsAsk?: SendingAsk;
}

/** Asks GET /api/account/sending for "What we send you". */
export function getSending(): Promise<PlaceAnswer<SendingPlace>> {
  return getPlace('/api/account/sending', readSendingPlace);
}

const ASK_STATES: ReadonlySet<string> = new Set([
  'sending',
  'waiting',
  'sent',
  'refused',
  'stopped',
]);

function readSendingAsk(value: unknown): SendingAsk | undefined {
  if (!isFields(value) || typeof value.state !== 'string' || !ASK_STATES.has(value.state)) {
    return undefined;
  }
  return only<SendingAsk>({
    state: value.state as SendingAsk['state'],
    choice: value.choice === 'on' || value.choice === 'off' ? value.choice : undefined,
    leadDays: numberOf(value.leadDays),
    sendTime: textOf(value.sendTime),
    code: textOf(value.code),
  });
}

/** Days or an instant and words, as the Worker gives them, each kept only where it is text. */
function readTexts<K extends string>(
  value: unknown,
  keys: readonly K[],
): Readonly<Partial<Record<K, string>>> | undefined {
  if (!isFields(value)) return undefined;
  const read = only(Object.fromEntries(keys.map((key) => [key, textOf(value[key])])));
  return Object.keys(read).length > 0 ? (read as Partial<Record<K, string>>) : undefined;
}

function readSetting(value: unknown): Setting | null {
  if (!isFields(value) || typeof value.category !== 'string') return null;
  if (!(SENDING_CATEGORIES as readonly string[]).includes(value.category)) return null;
  const email =
    value.email === 'on' || value.email === 'off' || value.email === 'not_offered'
      ? value.email
      : null;
  const switched =
    value.switch === 'offered' || value.switch === 'locked' || value.switch === 'none'
      ? value.switch
      : null;
  if (email === null || switched === null) return null;
  const locked =
    value.locked === 'one_way' || value.locked === 'email' || value.locked === 'other'
      ? value.locked
      : undefined;
  return only<Setting>({
    category: value.category as SendingCategory,
    homeId: textOf(value.homeId),
    email,
    byText: value.byText === true ? (true as const) : undefined,
    locked,
    switch: switched,
    leadDays: numberOf(value.leadDays),
    sendTime: textOf(value.sendTime),
    held: readTexts(value.held, ['from', 'until']),
    changed: readTexts(value.changed, ['on', 'by', 'how']),
    officeCalls: value.officeCalls === true ? (true as const) : undefined,
    ask: readSendingAsk(value.ask),
  });
}

function readSendingSection(value: unknown): SendingSection | null {
  if (!isFields(value)) return null;
  const settings = listOf(value.settings, readSetting);
  if (settings.length === 0) return null;
  return only({ homeId: textOf(value.homeId), label: textOf(value.label), settings });
}

function readSendingPlace(body: unknown): SendingPlace | null {
  if (!isFields(body) || typeof body.today !== 'string') return null;
  const { household } = body;
  if (!isFields(household) || typeof household.id !== 'string') return null;
  const role = body.role === 'client' || body.role === 'member' ? body.role : null;
  if (role === null) return null;
  const email = isFields(body.email) ? textOf(body.email.state) : undefined;
  const texts = isFields(body.texts) ? body.texts : null;
  const textsState = textOf(texts?.state);
  const hours = isFields(body.textHours) ? body.textHours : null;
  const from = textOf(hours?.from);
  const until = textOf(hours?.until);
  const summary = isFields(body.summary) ? body.summary : null;
  const every = summary?.every === 'each' || summary?.every === 'daily' ? summary.every : null;
  return only<SendingPlace>({
    today: body.today,
    household: readHousehold(household.id, household),
    role,
    email: email === undefined ? undefined : { state: email },
    texts:
      textsState === undefined
        ? undefined
        : only({ state: textsState, turnOnBy: textOf(texts?.turnOnBy) }),
    paused: readTexts(body.paused, ['from', 'until', 'home']),
    textHours: from !== undefined && until !== undefined ? { from, until } : undefined,
    summary:
      every === null
        ? undefined
        : only<NonNullable<SendingPlace['summary']>>({ every, time: textOf(summary?.time) }),
    sections: listOf(body.sections, readSendingSection),
    textsAsk: readSendingAsk(body.textsAsk),
  });
}

/** Her switch for one kind of message, as the request door takes it (11.3, "Up."). */
export const PREFERENCES_SET = 'preferences.set';

/** Her texts turned off, every one, as the request door takes it (11.3, "Up."). */
export const TEXTS_STOP = 'texts.stop';

/**
 * Her choice of one kind of message by email, for one home or for every home, sent through the
 * request door like any ask, `preferences.set`, under the key the switch minted for it: the whole
 * row as her page holds it, so for reminders always the days before and the time of day (11.3,
 * "Up."). The door's answer is read as any ask's (`AskAnswer`); the switch stays as ops last said
 * until ops's push says otherwise (CLAUDE.md binding rule 1).
 */
export function sendPreference(
  key: string,
  household: string,
  choice: {
    readonly category: SendingCategory;
    readonly homeId?: string;
    readonly email: 'on' | 'off';
    readonly leadDays?: number;
    readonly sendTime?: string;
  },
): Promise<AskAnswer> {
  return sendRequest({
    key,
    type: PREFERENCES_SET,
    household,
    payload: only({
      category: choice.category,
      home_id: choice.homeId ?? null,
      channel: 'email',
      choice: choice.email,
      lead_days: choice.leadDays,
      send_time: choice.sendTime,
    }),
  });
}

/** Her texts turned off, every one, `texts.stop`, with no payload (11.3, "Up."). */
export function stopTexts(key: string, household: string): Promise<AskAnswer> {
  return sendRequest({ key, type: TEXTS_STOP, household, payload: {} });
}

/*
 * STRIPE'S PAGE, ASKED FOR AND WAITED ON. Once the door has taken her ask to pay an invoice
 * (`invoice.pay`), the screen asks the Worker where it stands every three seconds, and the Worker
 * answers from what it has stored, never waiting on ops (the seam, l.665-675 at b727ec1).
 */

/** Where her ask for Stripe's page stands, as the screen acts on it. */
export type PayPageAnswer =
  /** Not yet with ops, or ops is making the page: ask again. */
  | { readonly outcome: 'waiting' }
  /** The page, at its https address: she goes there. */
  | { readonly outcome: 'ready'; readonly url: string }
  /** No page is coming of this ask: she may press again, which is a new ask. */
  | { readonly outcome: 'none' }
  /** Ops refused it as final, with its word, or home's own `expired` for an ask too late to send. */
  | { readonly outcome: 'refused'; readonly code?: string }
  /** Home ran out of attempts sending it. */
  | { readonly outcome: 'failed' }
  /** 401: the session has ended, and the door is the way back in. */
  | { readonly outcome: 'signed_out' }
  /** 404: the ask or the invoice is not hers to ask about now. */
  | { readonly outcome: 'not_found' }
  /** The poll did not go through, or said what this build cannot read: ask again. */
  | { readonly outcome: 'unread' }
  /** The poll never reached the Worker, because the connection dropped (`within`): ask again. */
  | { readonly outcome: 'offline' }
  /** The poll had no answer within its time (`REQUEST_LIMIT_MS`), and was let go. */
  | { readonly outcome: 'stalled' };

/**
 * Asks GET /api/invoices/:id/page where her ask for Stripe's page stands, within
 * `REQUEST_LIMIT_MS`. The invoice is the path's last part but one, encoded, and the ask and the
 * household it went up under are the query, so whatever each holds names one of each and never
 * another path. A page is kept only at an https address, so the screen only ever sends her to a web
 * page, and nothing else of it is read. Every answer but a 200's is read for its status alone, and
 * its body read to its end and let go (`letGo`).
 */
export async function getPayPage(
  invoiceId: string,
  askId: string,
  household: string,
): Promise<PayPageAnswer> {
  const query = new URLSearchParams({ ask: askId, household });
  try {
    return await within(
      `/api/invoices/${encodeURIComponent(invoiceId)}/page?${query}`,
      { headers: { accept: 'application/json' } },
      readPayPage,
    );
  } catch (error) {
    const why = unansweredBy(error);
    return why === null ? { outcome: 'unread' } : { outcome: why };
  }
}

/**
 * Asks GET /api/payments/setup where her ask for Stripe's page to save a way to pay with nothing
 * owed stands (ops's section 12.2, round five, slice A5), within `REQUEST_LIMIT_MS`, her ask and the
 * household it went up under as the query, each encoded, and the answer read as a pay's page is.
 */
export async function getSetupPage(askId: string, household: string): Promise<PayPageAnswer> {
  const query = new URLSearchParams({ ask: askId, household });
  try {
    return await within(
      `/api/payments/setup?${query}`,
      { headers: { accept: 'application/json' } },
      readPayPage,
    );
  } catch (error) {
    const why = unansweredBy(error);
    return why === null ? { outcome: 'unread' } : { outcome: why };
  }
}

/** What the Worker's answer says of her ask for Stripe's page. */
async function readPayPage(response: Response): Promise<PayPageAnswer> {
  if (response.status !== 200) {
    await letGo(response);
    if (response.status === 401) return { outcome: 'signed_out' };
    if (response.status === 404) return { outcome: 'not_found' };
    return { outcome: 'unread' };
  }
  const body: unknown = await response.json();
  if (!isFields(body)) return { outcome: 'unread' };
  switch (body.state) {
    case 'sending':
    case 'creating':
      return { outcome: 'waiting' };
    case 'ready': {
      const url = httpsOf(body.url);
      return url === undefined ? { outcome: 'none' } : { outcome: 'ready', url };
    }
    case 'none':
      return { outcome: 'none' };
    case 'refused':
      return only({ outcome: 'refused' as const, code: textOf(body.code) });
    case 'failed':
      return { outcome: 'failed' };
    default:
      return { outcome: 'unread' };
  }
}

/*
 * ONE OF AN INVOICE'S PDFS, ASKED FOR AND WAITED ON (ops's section 11.2, round five's slice B3,
 * P62). Her press goes up through the request door as `invoice.document`, and the screen asks the
 * Worker where it stands every three seconds, as for Stripe's page, and the Worker answers from what
 * it has stored, never waiting on ops. Once the Worker holds the PDF, read from ops by the drain of
 * her press, her tab goes to home's own address for it (`documentFile`), never to an address of
 * ops's, which no answer is read for (ops #76, ask 4).
 */

/** Her press for one of an invoice's PDFs, as the request door takes it (section 11.2). */
export const DOCUMENT_ASK = 'invoice.document';

/**
 * Asks for one of an invoice's PDFs through the request door, `invoice.document` with the invoice
 * and the document, under the key the press minted, within `REQUEST_LIMIT_MS`. The door's answer is
 * read as any ask's (`AskAnswer`), ops's own `document_not_ready` among its refusals.
 */
export function askForDocument(
  key: string,
  household: string,
  invoiceId: string,
  documentId: string,
): Promise<AskAnswer> {
  return sendRequest({
    key,
    type: DOCUMENT_ASK,
    household,
    payload: { invoice_id: invoiceId, document_id: documentId },
  });
}

/** Where her press for a PDF stands, as the screen acts on it. */
export type DocumentAnswer =
  /** Not yet with ops, or the PDF still on its way to home: ask again. */
  | { readonly outcome: 'waiting' }
  /** Home holds the PDF for her: her tab goes to home's own address for it (`documentFile`). */
  | { readonly outcome: 'ready' }
  /** Ops refused it as final, with its word, or home's own `expired` for one too late to send. */
  | { readonly outcome: 'refused'; readonly code?: string }
  /** It did not go through, or ops's answer brought no link the Worker hands out. */
  | { readonly outcome: 'failed' }
  /** 401: the session has ended, and the door is the way back in. */
  | { readonly outcome: 'signed_out' }
  /** 404: the press or the invoice is not hers to ask about now. */
  | { readonly outcome: 'not_found' }
  /** The poll did not go through, or said what this build cannot read: ask again. */
  | { readonly outcome: 'unread' }
  /** The poll never reached the Worker, because the connection dropped (`within`): ask again. */
  | { readonly outcome: 'offline' }
  /** The poll had no answer within its time (`REQUEST_LIMIT_MS`), and was let go. */
  | { readonly outcome: 'stalled' };

/**
 * Asks GET /api/invoices/:id/document where her press for one of its PDFs stands, within
 * `REQUEST_LIMIT_MS`, the invoice the path's last part but one, encoded, and the press and the
 * household the query, as `getPayPage` asks. Only its state is read: an address in the answer, of
 * anybody's, is never read, so her tab can go nowhere but home's own (`documentFile`).
 */
export async function getDocument(
  invoiceId: string,
  askId: string,
  household: string,
): Promise<DocumentAnswer> {
  const query = new URLSearchParams({ ask: askId, household });
  try {
    return await within(
      `/api/invoices/${encodeURIComponent(invoiceId)}/document?${query}`,
      { headers: { accept: 'application/json' } },
      readDocument,
    );
  } catch (error) {
    const why = unansweredBy(error);
    return why === null ? { outcome: 'unread' } : { outcome: why };
  }
}

/** What the Worker's answer says of her press for a PDF. */
async function readDocument(response: Response): Promise<DocumentAnswer> {
  if (response.status !== 200) {
    await letGo(response);
    if (response.status === 401) return { outcome: 'signed_out' };
    if (response.status === 404) return { outcome: 'not_found' };
    return { outcome: 'unread' };
  }
  const body: unknown = await response.json();
  if (!isFields(body)) return { outcome: 'unread' };
  switch (body.state) {
    case 'sending':
      return { outcome: 'waiting' };
    case 'ready':
      return { outcome: 'ready' };
    case 'refused':
      return only({ outcome: 'refused' as const, code: textOf(body.code) });
    case 'failed':
      return { outcome: 'failed' };
    default:
      return { outcome: 'unread' };
  }
}

/**
 * HOME'S OWN ADDRESS FOR ONE OF AN INVOICE'S PDFS (ops #76, ask 4): the PDF the drain of her press
 * read from ops and the Worker holds for her, `GET /api/invoices/:id/documents/:document_id/file`,
 * on the household the press went up under, each part encoded, so it names one invoice and one PDF
 * and never another path. The Worker answers it as a PDF to open in the tab, as often as the tab
 * asks in its ten minutes, and then one plain line saying to press the PDF again.
 */
export function documentFile(invoiceId: string, documentId: string, household: string): string {
  const query = new URLSearchParams({ household });
  return `/api/invoices/${encodeURIComponent(invoiceId)}/documents/${encodeURIComponent(documentId)}/file?${query}`;
}
